/**
 * The mountain. An analytic heightfield — physics samples it directly at any point
 * with no mesh lookup, and rendering tessellates the same function at whatever LOD
 * the view needs. Trails are carved by a signed-distance overlay (see trails.ts) so
 * the ridden surface and the collision surface are the same function by construction.
 *
 * ---------------------------------------------------------------------------------
 * ROUND 9: the mountain had no top, and five rounds of azimuthal relief could not have
 * given it one. This round adds RADIAL structure and one exact harmonic, and it changes
 * the instrument first because the old one could not see the gap.
 *
 * THE GAP, verbatim: "k=1 azimuthal relief is 5.5 m of 63.1 m and did not move this round.
 * The mountain has no high side and no low side, so it has no summit, no shoulder and no
 * silhouette — every capture reads as broad rolling open ground. farhill_13p0s looks
 * straight at SUMMIT GATE, 747 m away, and the skyline above it is horizontal."
 *
 * THE INSTRUMENT FIRST, because it reversed the plan.
 *
 * Every landform invariant in this file measures a CONTOUR RING. Four rounds have moved a
 * ring statistic without moving a screenshot, and R8 was the clearest case: +67% on the
 * k=5-12 spur band, and the reply was still "broad rolling open ground". So R9 built the
 * instrument that measures the picture — a horizon ray-march (skyline.mjs, and
 * `terrain.flatSkylineFrames` at boot): for every azimuth, the largest elevation angle any
 * ground subtends inside the 3.2 km far plane, which is exactly where terrain meets sky in
 * the frame. It is validated by falsification on three analytic worlds whose answers are
 * known in closed form — a cone, the same cone with a summit block, the same cone tilted.
 *
 * WHAT IT SAID ABOUT THE SHIPPED R8 FUNCTION, on the shipping seed:
 *
 *   skyline from the trailhead, full 360               16.95 deg
 *   worst 65-deg window, i.e. one screenshot            1.33 deg   <- the horizontal line
 *   the summit's own elevation angle                    8.7 deg
 *   the nearest intervening rise                       10.2 deg    <- the summit is BEHIND it
 *
 * The summit was not on the skyline from the trailhead. It was hidden behind ground 368 m
 * away. That is the whole of "no summit", and no ring statistic in this file could say it.
 *
 * AND THE SWEEP THAT KILLED THE OBVIOUS FIX. Tripling both aspect octaves — azimuthal RMS
 * 63 -> 147 m, more than twice what any critic has asked for — moves the worst 65-degree
 * window from 1.33 to 0.60 deg. It gets WORSE. That is structural, not a tuning result:
 * `s` is a function of DIRECTION ONLY, so along any outward ray the ground descends
 * monotonically at a rate that is constant on that ray, and no amplitude of `s` can put
 * ground above the eye in the downhill half. Five rounds of "add azimuthal relief" were
 * pushing on a term that cannot reach the defect. See CAP_R.
 *
 * FOUR CHANGES, three of them radial:
 *
 * THE CAP (see CAP_R). The radial profile had exactly one feature in it — a constant
 * gradient — with its FLATTEST part at the apex, because APEX_FRAC rounds the top. The
 * summit was the flattest ground on the mountain. The profile's gradient is now multiplied
 * up inside R = 1400 m into a summit block, by a factor that is >= 1 everywhere and C1 at
 * both ends, so it can only steepen: dh/dR keeps the profile's sign and no basin can open,
 * at any amplitude. CAP_R is a WORLD radius, not an Reff, so with the trail network's top
 * node at R = 1740 m the cap cannot move one metre of built trail whatever `s` does.
 *
 * THE TILT (see TILT_AMP). k=1 is authored rather than sampled: (dir . axis) is
 * cos(theta - theta0) exactly. There was no amplitude anywhere in this file that k=1 was a
 * function of, which is why two rounds could not move it and could not see that they had
 * not. It is also the cheapest azimuthal relief here by a factor of nine — cross-slope
 * gradient goes as k times amplitude — so it is now the largest single azimuthal term.
 *
 * THE OUTLIERS (see FOOT_AMP). The drainage network SUBTRACTS, so the highest ground on
 * any far ring is the un-incised datum, which is the axisymmetric profile: the silhouette
 * of an eroded cone is the cone. The far field beyond the massif's foot now gets an
 * ADDITIVE, non-negative hill field, double-gated on world radius AND Reff so it cannot
 * reach the belt or sit on a still-live flank.
 *
 * THE MASSIF MOVED, 600 m, and it had to. findSpawn is an argmax with an altitude bonus
 * and it is knife-edged: at the R8 offset the R9 field puts the trailhead at 781 m — 91 m
 * above the vegetation piece's treeline — on a 46% opening pitch that stops falling after
 * 56 m. 289 offsets on a 60 m grid ranked by R6's six-criterion penalty; MASSIF_X moves
 * 480 m and MASSIF_Z 360 m. Result: 675 m (15 m under the treeline), ground falling for
 * the whole 400 m probe at 14.7% with no 60 m pitch over 19.3%.
 *
 * WHERE R9 LANDS, shipping seed, R8 -> R9:
 *
 *   summit elevation angle from the trailhead   8.7 deg -> 16.9 deg, and it is now the
 *                                               highest thing on the skyline rather than
 *                                               8 deg below the rise in front of it
 *   k=1 azimuthal relief                        5.5 m -> ~56 m
 *   azimuthal RMS 1.4-2.6 km                    63.1 m -> ~83 m
 *   true peak                                   1051 m -> ~1430 m
 *
 * WHAT IS STILL WRONG, so the next round does not have to find it twice. The DOWNHILL half
 * of the panorama is still a smooth ramp: 4.6 deg of skyline across the frame, and its
 * shape is a tilt, not a ridgeline. The cause is now understood and it is not azimuthal
 * amplitude — the profile is CONVEX below the mid-mountain (gradient falls through the
 * runout), and a convex slope occludes its own base, so from the trailhead the ground
 * 700 m downhill hides everything past it. Fixing that needs the profile to be CONCAVE
 * over the belt — steep upper mountain, gentle lower — which is a change to the ridden
 * gradient and belongs in a round that owns the ride gate, not this one.
 *
 * ---------------------------------------------------------------------------------
 * ROUND 8: R7 doubled the azimuthal relief and the massif was still a cone, because the
 * relief went where a cone already is. And R7's channels never joined; their count fell
 * only because every one of them got fatter.
 *
 * TWO INSTRUMENT FAILURES FIRST, because both changed a conclusion in this round.
 *
 * ONE: THE SEED. Every landform probe in this repo runs `new Terrain(1337)`. The game runs
 * `Tuning.world.seed = 20260805`. Distributional numbers (spectra, counts, grade) are fair
 * on either — but the SPAWN is an argmax over one realisation, and so is any stall census.
 * Measured on 1337 this round's function stalls 6 walks in 384; on the shipping seed it
 * stalls 17 in 576 and R7 stalls 31. The R7-vs-R8 conclusion REVERSED between seeds. Every
 * number below is on the shipping seed, and any probe that quotes a location, an argmax or
 * a stall count and does not name its seed is quoting a different world.
 *
 * TWO: THE COUNT METRIC CANNOT SEE THE DEFECT IT WAS BUILT FOR. R7 declared
 * CHANNEL_CONVERGENCE — channels crossing a low contour ring over channels crossing a high
 * one — and shipped 0.98 on the seed that matters, i.e. a flat comb. Even a good value
 * would not have meant what it says: on three synthetic mountains where the answer is known
 * by construction (captures/w5-terrain-r8-tools/selftest.mjs), PARALLEL / FATTEN /
 * CONFLUENT give counts of 24->24, 24->12, 24->12. The metric scores "half the channels
 * faded out and the rest got wider" and "half the channels merged into the other half"
 * IDENTICALLY. A tracer that follows each floor outward and asks whether the divide between
 * two LIVE channels collapsed gives 0 / 0 / 12 on the same three. That tracer is now
 * terrain.channelConfluence and it runs at boot.
 *
 * WHAT R7 ACTUALLY WAS, on the shipping seed:
 *
 *   azimuthal RMS 1.4-2.6 km                65.0 m, of which 80.1% is k <= 3
 *   the k=5-12 band, where spurs live       21.9 m  (R6: ~21 m — four rounds, unmoved)
 *   channel count ratio far/near            0.98
 *   channels that JOIN another downhill     10 of 33
 *   lateral drift over 1.7 km of descent    0.68 of a channel spacing = 5 degrees
 *
 * ONE COMBED CONE, measured three ways. Two changes:
 *
 * ASPECT GETS A SECOND OCTAVE (see ASPECT2_SCALE). A noise circle of radius 0.9 crosses
 * FOUR features per revolution and can be nothing but a four-lobed cone, whatever its
 * amplitude — which is why R7's 32 -> 65 m bought nothing the critic could see. A circle
 * of radius 3.0 crosses about nine. That number was measured (captures/.../azspec.mjs) and
 * not reasoned to: the first draft of this round used 1.9 on the theory that circumference
 * over lattice spacing gives the wavenumber, and it put its energy in k=1-3 — a bigger
 * cone. Because the profile is evaluated at Reff = R * s(direction), these are ridges of
 * the mountain's BODY: the ground on that bearing is higher from summit to runout and meets
 * the valley floor further out. A relief field added on top can only ever be texture ON a
 * cone, which is the whole content of "still a body of revolution".
 *
 * THE WHOLE DOMAIN TWISTS (see TWIST). Every field is now evaluated at a query point
 * rotated about the summit by an angle that grows with radius and varies with azimuth. The
 * first draft of this round bent only the network's own trace, which works — an ablated
 * network-only sector map shows channels visibly curving and bunching — and is INVISIBLE in
 * the full field, because the drainage ribs, the side gullies, the benches and the aspect
 * spurs are all still radial and they are what the eye reads.
 *
 * The twist is free of basin risk, and provably rather than budgeted: for each fixed R,
 * theta -> theta + phi is a diffeomorphism of the circle, so h and the untwisted H have
 * critical points in bijection and every descending path maps to a descending path with an
 * IDENTICAL height profile. No pit can be created at any amplitude. The precondition —
 * 1 + dphi/dtheta > 0, past which the ring folds and the fold is a new pair of critical
 * points — is measured at build time as terrain.twistMonotone, not assumed.
 *
 * A twist alone cannot MERGE two channels either, for exactly the same reason: it squeezes
 * the interfluve between them thinner without lowering it. NET_MELT is the other half —
 * the network amplitude is modulated by w^2 of the same field that drives the twist, and
 * w^2 is at its minimum on w's rising edges, which is precisely where the twist is driving
 * channels together. They meet and their divide is gone.
 *
 * WHERE R8 LANDS, shipping seed, R7 -> R8:
 *
 *   azimuthal RMS 1.4-2.6 km                 65.0 m -> 63.1 m   (13.8% of radial relief)
 *   share of it in k <= 3                     80.1% -> 49.9%
 *   the k=5-12 spur band                     21.9 m -> 36.6 m   (+67%)
 *   channel count ratio far/near               0.98 -> 0.70
 *   channels that JOIN another downhill    10 of 33 -> 14 of 36
 *   lateral drift, in channel spacings         0.68 -> 1.34
 *   rider-scale descent walks that STALL   31 of 576 -> 17 of 576
 *   fall line launching a wheel below 60 km/h  1.95% -> 1.82%
 *   13% traverse launching below 60 km/h       2.86% -> 3.13%
 *   belt grade p25/50/75/90                17/25/38/54 -> 18/26/39/55 %
 *   rock fraction of the belt                 17.1% -> 18.2%
 *   cost of one full terrain sample        0.87-1.01x, order-dependent: not resolvable
 *
 * That last row is an interleaved A/B in one process, seven repeats, best of each, run both
 * ways round. It adds three noise evaluations and removes one, and the three it adds are
 * all sampled on the UNIT DIRECTION — a four-cell neighbourhood of the lattice that is
 * always in L1 — while the one it removes was world-space and missed cache every time. The
 * honest statement is that this round's cost change is inside the noise of the instrument.
 *
 * AND THE SPAWN MOVED, as it does every time a landform constant moves, and this time it
 * was not close: at R7's offset the R8 field puts the trailhead at 758 m — 68 m above the
 * vegetation piece's treeline — on a 56% opening pitch with a 75% worst 60 m. A capture
 * from there falls off the mountain. 625 offsets on a 25 m grid, ranked by penalty against
 * six criteria (R6's method), found two clean ones; MASSIF_X moves 300 m and MASSIF_Z does
 * not move at all. The result passes all six where R7's own spawn passed five: 682 m
 * (8 m under the treeline), 2.05 km from the summit, ground falling for the whole 400 m
 * probe along the heading the rider is given, at 17.3% with no 60 m pitch over 27%.
 *
 * WHAT IS STILL WRONG, so the next round does not have to find it twice. The channels bend
 * and join, but the bend is 8 degrees and on a detrended sector map it reads as tapering
 * and pinching rather than as a dendritic Y. The bound is exact and it is TWIST_MONOTONE:
 * a bijective twist with L lobes cannot exceed about 0.7/L radians, so more bend costs
 * lobes and more lobes cost bend. Getting a visibly dendritic network past that needs a
 * term that changes the ring's topology on purpose — a fold, or a second network generation
 * gated on the first — and it needs the basin question answered again from scratch.
 *
 * ---------------------------------------------------------------------------------
 * ROUND 7: the massif was a surface of revolution combed by radial spokes, and four
 * rounds of amplitude changes could not fix it because the DOMAIN MAP is what makes
 * everything a spoke. This round changes the topology instead of the amplitude.
 *
 * THE CARRIED GAP, re-measured here with an instrument written from scratch against the
 * shipped R6 function (captures/w5-terrain-tools/measure.mjs), which reproduces the
 * critic's three numbers independently:
 *
 *   azimuthal deviation from a body of revolution, 1.4-2.6 km   31.6 m RMS  (critic ~32)
 *     against 537 m of radial relief                            = 5.9%
 *   channels crossing a contour ring, >4 m prominence           37 at R=700 -> 74 at 2600
 *     (critic: 37 at 700 -> 92 at 2400)                         RISES x2.0
 *   channel wander over 350 m of fall line from R=1600          61.5 m median
 *
 * That third row is not the critic's number and the difference is an INSTRUMENT BUG in the
 * first version of this round's own probe, caught before it was quoted. The tracker
 * followed a channel outward inside a fixed +-3-sample window and DROPPED any channel that
 * reached the edge of it — i.e. it discarded exactly the channels that bend most, and
 * reported a median built from the straightest 60%. It read 12.3 m on R6 and 16 m on R7,
 * which would have made this round look like it had barely moved the thing it was sent to
 * fix. Sized to half the local channel spacing and dropping nothing, the same probe reads
 * 61.5 m on R6 and 132.8 m on R7. Both numbers are twice what they were; the RATIO to the
 * channel spacing is what did not move, and that is the honest remaining gap.
 *
 * WHY IT RISES, which nobody had measured. Ablating the rill (RILL_AMP 60 -> 0) takes the
 * count from 37->74 to 30->32. The rill is the whole rise, and NOT because it adds
 * channels downhill: its own channel count is nearly constant (its noise-space trace
 * radius is R * (ref/rs)^0.9375 / RILL_SCALE, i.e. R^0.0625 — flat). It rises because the
 * rill's AMPLITUDE grows as R^0.9375, so at R=700 most of its 79 channels are under 4 m
 * deep and do not count, and by R=2600 nearly all of them are. Four rounds of "add more
 * rill" therefore had to make the number worse, and R6's amplitude tripling did exactly
 * that. The count metric was never about amplitude; it is about the domain map.
 *
 * WHY THE DOMAIN MAP CANNOT PRODUCE CONVERGENCE. Every field in this file lives in
 * q = p * k(R) with k = (ref/rs)^(1-rho). That map is a pure radial scaling, so the number
 * of features around a contour ring goes as R/(SCALE * (R/ref)^(1-rho)) = R^rho, and rho is
 * pinned at 1/8 (drainage) or 1/16 (rill) BECAUSE THE SAME EXPONENT SETS THE ELONGATION —
 * the map's radial derivative is exactly rho times its tangential one. Count and elongation
 * are the same constant. Asking for channels that converge downhill (count falling) out of
 * a map whose count rises as R^rho is asking the impossible, and no amplitude fixes it.
 *
 * WHAT REPLACES IT: AN ANNULUS TRACE. The drainage network is now evaluated at
 *
 *     Q(p) = p * (ref/rs)^(1+M) / NET_SCALE  +  W(p) * t^(p)
 *
 * where t^ is the unit tangent. The first term is a point on a circle in noise space of
 * radius |Q| = R * (ref/rs)^(1+M) / NET_SCALE, which for M > 0 SHRINKS as R grows: the
 * ring of ground at R=2600 traces a smaller circle through the noise than the ring at
 * R=700, so it crosses fewer features. Channel count falls as R^-M, by construction, at
 * every amplitude and every threshold. That is the entire fix, and it is one exponent.
 *
 * The elongation is now a free parameter rather than the same number: features are 1/M
 * times longer down the hill than across it (M = 1/8 -> 8:1). The two used to be locked
 * together, one exponent for both, which is why no previous round could have had both.
 *
 * WHAT M COSTS, because it is not free and the first draft of this round paid it and
 * failed: a generation's RADIAL gradient leak is its cross-slope gradient times M, and
 * radial gradient is what opens basins. The first draft ran M = 3/16 at NET_GRAD 0.34 and
 * measured 13 of 32 rider-scale descents from R = 1600 STALLING in closed bowls, against
 * R6's 0 of 32. The pointwise "fraction of the belt pointing uphill" proxy only read 3.8%
 * and would not have stopped it — the descent walk is the instrument that matters here,
 * and it is R2's and R3's, recovered.
 *
 * The second term is the AZIMUTHAL WARP the brief names. W is world-space noise at
 * NET_WARP_SCALE, so it varies DOWN the fall line — the previous warp was borrowed from
 * the drainage field's own warp, which is 8:1 elongated and therefore has a 21 km radial
 * wavelength, i.e. it modulated channel SPACING and bent nothing. That is why wander
 * measured 12 m over 350 m. Displacing along t^ by W moves a channel sideways by
 * NET_SCALE * W / gN metres, and because W varies with azimuth as well as radius,
 * neighbouring channels bend by different amounts: they converge, merge and pinch out.
 *
 * The price of bending is exact and worth writing down, because it is what bounds
 * NET_WARP: a channel whose floor runs at b degrees off the fall line contributes
 * gradientCross * sin(b) of RADIAL gradient, and radial gradient is what opens basins.
 * At NET_GRAD 0.34 and a 10 degree bend that is 0.06 against the profile's 0.175. Measured,
 * NET_WARP 0.22 puts 2 of 48 descents into a bowl and 0.14 puts 0 — so the warp is at the
 * largest value that keeps the mountain descendable, and that, not the look, is what
 * bounds it.
 *
 * TWO GENERATIONS, because one is not a network. The trunk field is gated so its
 * tributary generation only exists on the INTERFLUVES (see NET_GATE) — fine channels run
 * down the spur flanks and terminate where they meet a trunk floor. That is both what a
 * drainage network looks like and the R5/R6 lesson applied correctly: it keeps fine
 * curvature off the trunk floors, which is exactly where a coasting bike goes. The
 * tributary's own wavenumber ratio falls with radius (NT_HI -> NT_LO), so the two
 * generations converge toward each other downhill — tributaries literally merging into
 * trunks — and the count ratio comes out well under 1.
 *
 * AND THE AZIMUTHAL DEVIATION, which the annulus trace alone does not fix. Ablation says
 * the whole 31.6 m is the main drainage field (12.1 m without it) and that the profile
 * carries all 537 m of relief as a function of R alone. A body of revolution plus a
 * zero-mean relief field IS a body of revolution to within the relief field's RMS, so no
 * amount of relief tuning gets this past ~10%. The profile itself is now evaluated at
 * Reff = R * s(direction): the mountain is a long gentle shoulder on some aspects and a
 * short steep face on others. It is FREE of basin risk in a way no relief term can be —
 * s depends only on direction, so dh/dR = P'(Reff) * s is negative wherever P' is,
 * everywhere, exactly. See ASPECT_AMP.
 *
 * WHERE R7 LANDS, same instrument, R6 -> R7:
 *
 *                                                     R6       R7
 *   azimuthal deviation / radial relief              5.9%     15.2%   (32 -> 65 m RMS)
 *   channel count ratio, R 0.6-0.8 km -> 2.4-2.8 km  1.89      0.98
 *   channel wander over 350 m from R=1600            61.5 m   132.8 m
 *   rider-scale descent from R=1600, mean / stalls   597 m/0  597 m/0
 *   fall line launching a wheel below 60 km/h        0.84%    0.50%
 *   13% traverse launching below 60 km/h             1.08%    0.34%
 *   belt grade p50 / p90                             33/77%   24/48%
 *   band-passed 50-150 m cross-slope RMS at R=1400   6.53 m   5.13 m
 *                                            R=2600  6.09 m   2.56 m
 *   cost of one full terrain sample                  1.00x    ~1.4x
 *
 * That last row is measured by an INTERLEAVED A/B in one process (perfab.mjs), because a
 * single-build timing on this machine moved 4x between runs under load and the first
 * reading it produced said R7 was FASTER, which is not credible for a function carrying
 * three more noise evaluations. Alternating the two builds inside one process, seven
 * repeats, best of each: R7 costs 1.32-1.54x an R6 sample, and reversing which build goes
 * first reproduces it (0.76x the other way). Three extra lattice evaluations out of
 * sixteen accounts for about half of it; the rest is the aspect and network arithmetic.
 * The frame cost is bounded by the clipmap's own 2.5 ms budget, which yields — so what it
 * actually buys is rings that refill about 40% slower when the rider is moving fast. The
 * captured `vista` reads medianFrameMs 2.1 and 120 draw calls.
 *
 * The last two rows are the honest cost. The trunk moved from a 150 m spacing to 380 m,
 * which is what bought the launch columns and the grade distribution, and the 50-150 m
 * band on the LOWER mountain went with it — the tributary generation holds that band at
 * the trailhead radius and is out at 180 m by R = 2.6 km. If a critic says the far-lower
 * mountain lost near-field texture between R6 and R7, they are right and this is the line.
 *
 * AND THE SPAWN MOVED, as it does every time a landform constant moves. Two things about
 * it are new this round. First, ASPECT raised the azimuthal-mean ground on the lower
 * mountain by ~86 m (Jensen: the profile is convex through the runout, so averaging over
 * aspects lifts it), which lifted every absolute elevation band and put findSpawn's argmax
 * at 810 m, on bare alpine ground — ASPECT_DATUM is the correction. Second, a vertical
 * datum shift is NOT neutral even though findSpawn's argmax is exactly invariant under
 * one: the TERRACE quantises absolute elevation, so shifting the mountain by 55 m re-phases
 * every bench by 1.67 periods and moves the local grades findSpawn scores. That cost a
 * sweep, and it is the kind of coupling that makes "just lower it" wrong here.
 *
 * ---------------------------------------------------------------------------------
 * ROUND 6: R5's rill launches a bike that CROSSES it, and the reason is one line of
 * calculus R5 never did. Same relief, three times less launch, and more landform.
 *
 * R5 shipped an incised rill field and argued it was safe like this: the ridged field is
 * SUBTRACTED, so its crease becomes the floor of a channel (concave — a compression) and
 * its broad minimum becomes the interfluve (convex, but shallow). Both halves of that are
 * true and both are irrelevant, because they are statements about the two EXTREMA of the
 * field and the bike spends its time on the FLANK between them.
 *
 * Write u = 1 - a for the smooth field underneath, where a is the rounded |noise|. R5's
 * term is h -= A*u^2, so
 *
 *     d2h/dx2  =  -2A (u')^2  -  2A u u''
 *                 ^^^^^^^^^^
 * and that first term is NEGATIVE — convex, i.e. launching — everywhere the field has any
 * slope at all, in proportion to the SQUARE of that slope. Squaring is a concave map, and
 * composing a concave map with a smooth field biases its whole flank convex. Measured on
 * the shipped R5 function, 8 straight tangential transects at the trailhead radius:
 * 62.1% of the rill's cross-slope profile is convex, median convex curvature 0.014 /m,
 * p90 0.060 /m — and 0.0353 /m is what throws a rolling wheel at 60 km/h.
 *
 * The paired attribution agrees and is the number that routed this round. At R5's own
 * launching traverse stations, re-evaluating the identical station under each ablation:
 *
 *     rill    removes 63.9% of the curvature      chatter    1.1%
 *     terrace          16.0%                      bench      1.9%
 *     drainage fine octaves 9.8%
 *
 * and the phase histogram says where: enrichment peaks at 1.57x on the flank (0.4-0.5 of
 * the way from divide to channel) and is 0.39x at the divide crown, 0.26x at the channel
 * floor. Exactly the flank term above. Not the crease R5 rounded, not the crown it
 * reasoned about.
 *
 * WHAT DOES NOT FIX IT, measured, because it is the obvious move: changing the exponent.
 * F = u^p with p = 1 kills the flank term outright (d2F/du2 = 0) and p < 1 makes the flank
 * compressive. At MATCHED cross-slope relief — which is the only honest comparison, since
 * lowering p shrinks the field's span and a smaller field always launches less — p = 1
 * buys 3.44% -> 2.60% and p = 0.75 buys 2.30%. Real, but small, and it costs the drainage
 * look (u^2 is what makes channels narrow and divides broad). The reason it is small is
 * conservation: a periodic profile of a given depth and wavelength has a FIXED total
 * convex turning, and the exponent only decides whether that turning is spread thinly
 * along the flanks or concentrated at the crowns. It cannot make it smaller.
 *
 * WHAT DOES FIX IT is the only term the turning argument leaves free. Convex curvature
 * goes as amplitude / wavelength^2 and visible relief goes as amplitude, so lengthening
 * the rill at proportional amplitude holds the relief and the cross-slope GRADIENT exactly
 * constant while dividing the curvature by the length ratio. RILL_SCALE 60 -> 150 with
 * RILL_AMP 24 -> 60 is that trade taken in full: 51 m -> 129 m of ground wavelength at the
 * trailhead, same 0.4 cross-slope gradient, same surface mix.
 *
 * Measured with the R5 instrument, 112 fall lines and 112 trail-shaped 13% traverses:
 *
 *                    fall line              13% traverse        tangential band RMS
 *                <25    <40    <60      <25    <40    <60      3-12  12-50  50-150
 *   R4          0.01%  0.09%  0.47%    0.00%  0.05%  0.66%     0.05   0.53   2.29
 *   R5          0.01%  0.24%  0.99%    0.02%  0.62%  3.44%     0.16   1.40   2.77
 *   R6          0.00%  0.09%  0.54%    0.00%  0.11%  0.85%     0.10   1.09   4.05
 *
 *   cross-slope relief per 600 m of contour, at R = 1.2 / 2.6 km
 *   R4  97.9 / 56.1      R5  101.2 / 67.6      R6  102.7 / 80.1   m
 *   rock fraction of the rideable belt
 *   R4  10.2%            R5  24.7%             R6  23.4%
 *
 * So R6 gives back three quarters of R5's traverse regression, halves its fall-line
 * launch, and is nonetheless the most landform this file has ever carried: 46% more
 * band-passed shape in the 50-150 m band than R5 and 77% more than R4, and 19% more
 * cross-slope relief at 2.6 km than R5. That last row is the standing "the far field has
 * no gully, rib or spur structure and reads as iso-elevation bands" critique — the rill is
 * the only term here whose features run DOWN the fall line, and this round roughly doubles
 * what it contributes at the distance that critique is about.
 *
 * WHAT IT COST, plainly: the 12-50 m tangential band goes 1.40 -> 1.09 m RMS. That is the
 * band the rill used to own and it now sits at 129 m instead of 51 m. 1.09 is still 2.1x
 * what R4 shipped, so this is not the billiard table R4 was — but if a critic says the
 * near field lost texture between R5 and R6, they are right and this is the line. It is
 * not recoverable: at 25 m wavelength the curvature budget at 60 km/h is about 1.1 m
 * peak-to-peak, which is 0.4 m RMS, and the band already carries more than that.
 *
 * IN THE HARNESS, and read the caveat before the numbers. A capture from one round cannot
 * be A/B'd against a capture from another: `src/bike/physics.ts` and `src/core/tuning.ts`
 * changed at 04:11 and `src/world/trails.ts` at 04:54, between the R5 capture (04:04) and
 * the R6 one (05:02). What IS clean is scoring the path the bike actually rode, out of each
 * capture's own telemetry, against that round's own terrain function — no cross-round
 * physics, no trail geometry, no renderer:
 *
 *   vista  worst convex curvature on the ridden line   R5 0.052 /m   R6 0.044 /m
 *          the speed that curvature launches at        R5 49.6 km/h  R6 53.5 km/h
 *          the speed the bike was actually doing       R5 49.6 km/h  R6 43.1 km/h
 *          maxAirTime / maxG from the harness          R5 0.79 s / 7.96 g   R6 0.28 s / 6.22 g
 *
 * R5's vista rode ground whose launch speed was exactly the speed it was travelling. R6's
 * has 24% of margin, and the harness records a third of the air and a quarter less load.
 *
 * The same probe on `coast` says something the next round should not re-derive: in BOTH
 * rounds the worst convex curvature on coast's ridden line launches at over 100 km/h
 * against a bike doing 50-56, and 0 of 1 (R5) and 0 of 4 (R6) recorded takeoffs have ground
 * under them convex enough to explain the takeoff at the speed recorded. **Coast's air is
 * not terrain-launched and never was.** That agrees with the wave-4 Stage A diagnosis
 * (rebound 0.00 m/s at 8 of 8 takeoffs, vGround negative at every one) and with its ranking
 * of scenario speed as the dominant contributor. Do not tune this file against coast.
 *
 * On the gate: `fullrun` ejection CAUSES moved, which is the part terrain owns. Going into
 * this round 4 of the first 6 were "thrown into the air"; now 2 of 6 are, at 0.26 s and
 * 0.24 s, and 4 of 6 are corridor departures at trailWeight 0 with the bike 3.6-4.0 m off a
 * 2.5 m corridor. Completion is still 1.5% and this file is no longer the reason.
 *
 * AND THE SPAWN MOVED AGAIN, exactly as MASSIF_X's note says it always does. Tripling the
 * rill's amplitude triples the height field's VARIANCE, so findSpawn's argmax finds more
 * high patches that happen to sit at 18%: at the old offset the trailhead jumped to 855 m,
 * above the treeline, 842 m from the summit, with an 85% pitch in its first 400 m. 1089
 * offsets on a 25 m grid, ranked by penalty rather than pass/fail (R5's own function
 * passes 1 in 169, so "0 of 841" for a candidate proves nothing on its own) — see
 * MASSIF_X.
 *
 * ---------------------------------------------------------------------------------
 * ROUND 5: R4's launch fix was paid for out of the only relief a rider can see, and
 * this round buys it back out of a budget R4 never spent.
 *
 * NOTE FROM R6: the traverse column at the bottom of this section is the honest cost R5
 * flagged for "the next round to look at first", and the next round did. The mechanism R5
 * gives for why the rill is safe is wrong — see ROUND 6 above. Everything else here about
 * the anisotropy budget stands and R6 is built on it.
 *
 * R4 stopped the bike flying and left a billiard table. Measured on the shipped R4
 * function, band-passed RMS of straight transects at the trailhead radius:
 *
 *                                   R3      R4      R5
 *   along the contour   3-12 m     0.12    0.04    0.16   m RMS
 *                      12-50 m     0.82    0.52    1.40
 *                     50-150 m     2.51    2.18    2.77
 *   down the fall line  3-12 m     0.02    0.01    0.01
 *                      12-50 m     0.20    0.10    0.13
 *   local extrema per 100 m of contour, 12-50 m band
 *                                  5.76    3.95    5.00
 *   cross-slope relief per 600 m of contour, at R = 2.6 km
 *                                  62.3    58.3    67.6   m
 *
 * Half a metre of shape across fifty metres of ground is a plane, and the captures said
 * so: a straight diagonal horizon with a texture under it.
 *
 * THE BUDGET, AND WHY IT IS TWO DIFFERENT NUMBERS. A wheel leaves the ground when
 * v^2 * kappa > g, so at 60 km/h the most convex curvature the ground may carry is
 * 0.035 /m. Curvature goes as amplitude / wavelength^2, so in the 3-12 m band that is
 * about 4 cm RMS — there is NO version of "relief you feel underneath you at 10 m" that
 * survives 60 km/h, and three rounds of this file spent themselves finding that out.
 *
 * But kappa is only charged ALONG THE DIRECTION OF TRAVEL, and this module has had the
 * mechanism that separates those two numbers since R2: the drainage domain map. A
 * feature 50 m across the hill is 800 m long down it at 16:1, so its along-track
 * curvature is 1/256 of its cross-track curvature. The eye reads the cross-track number
 * — swales and ribs fanning out to either side, and a horizon that is no longer a
 * straight line — and the wheel pays the along-track one. That is the entire round.
 *
 * FOUR THINGS, in the order they matter:
 *
 *   RILLS (see RILL_SCALE). One octave, ~50 m across the fall line, 16:1 elongated down
 *     it, SUBTRACTED so the ridged field's crease is the floor of a channel (concave — a
 *     compression, not a launch) and its broad minimum is the interfluve.
 *   ONE OCTAVE, not three. Three measured 3x worse on every launch column, because
 *     ridgedMF weights each octave by the previous one and after the sign flip that puts
 *     the fine detail in the CHANNEL FLOORS — which is exactly where a fall line goes.
 *     Water and a coasting bike pick the same route, so the octave stack aims its
 *     curvature at the one path that has to be smooth.
 *   RILL_RHO 0.0625 rather than the drainage field's 0.125. One extra square root on a
 *     chain the map already builds; quarters the along-track curvature and halves the
 *     radial gradient leak that puts flat spots in the fall line.
 *   AN ASYMMETRIC TERRACE (see TERRACE_PERIOD). The quantiser was a symmetric quintic,
 *     so the nose of each roll and the bottom of it had the same curvature. Only the
 *     nose can throw a wheel. S'(f) = 12f(1-f)^2 drops the convex peak from 5.77 to 4.00
 *     and RAISES bench depth 52%, which paid for the period going 48 -> 33 at unchanged
 *     worst-case curvature: benches 1.45x more often AND deeper.
 *
 * Measured against R3 and R4 with the same instrument — 48 smooth 600 m fall lines and
 * 48 trail-shaped 13% traverses, the real four-probe wheel envelope, worst convex
 * curvature over every window from 1.6 to 25.6 m, reported as the fraction of the line
 * whose launch speed is under 25/40/60 km/h:
 *
 *                        fall line              13% traverse
 *                    <25    <40    <60      <25    <40    <60
 *   R3              0.50%  1.58%  3.46%    0.90%  2.98%  6.50%
 *   R4              0.02%  0.08%  0.44%    0.00%  0.08%  0.71%
 *   R5              0.00%  0.27%  0.98%    0.02%  0.68%  3.50%
 *   R5, rills off   0.02%  0.10%  0.52%    0.00%  0.17%  0.94%   <- the terrace change alone
 *
 * The traverse column is the one to watch and it is the honest cost of the round: an
 * anisotropic field is nearly free to anything running down the fall line and is charged
 * in full to anything crossing it, and a built trail crosses it. 3.5% against R4's 0.71%
 * is a real regression and it is where the next round should look first if `fullrun`
 * turns out to need it. It is still half of what R3 shipped.
 *
 * So R5 gives back roughly a fifth of what R4 bought on the fall line and takes 2.7x
 * the visible relief
 * for it, and the harness agrees where it counts: `coast` — no rider input at all, the
 * scenario that recorded 2.0 s of air and 20 g going into this wave — reads 0.23 s and
 * 3.9 g at 56 km/h, against R4's own 0.23 s and 4.4 g at 52 km/h. Same air, less load,
 * four more km/h.
 *
 * WHAT THIS ROUND DID NOT FIX, so nobody re-derives it: the 3-12 m band DOWN the fall
 * line is still 0.01 m and it is staying there. That is the band the budget above
 * forbids, and R4 was right about it. Near-field shape that the rider feels through the
 * bars has to come from the trail overlay, which knows how fast the line it built is
 * meant to be ridden; this function does not and cannot.
 *
 * AND THE TRAP THAT COST AN HOUR: every landform change moves the spawn, because
 * main.ts picks it as an argmax with an altitude bonus. The terrace change ALONE put the
 * trailhead at 756 m on bare scree with a six-metre opening descent. See MASSIF_X.
 *
 * ---------------------------------------------------------------------------------
 * ROUND 4: the mountain was launching a coasting bike, and why it was not the octave
 * everybody suspected.
 *
 * The wave-4 gate is "a bike coasting must not fly", and `coast` records 2 s of air and
 * 20 g on raw mountain with no rider input. The standing suspicion was that terrain's
 * high-frequency fbm octaves put content at the wheel's own contact scale. Measured, that
 * is wrong twice over, and both errors are worth keeping written down.
 *
 * The instrument: a rigid 371 mm wheel rolled along smooth fall-line paths over the exact
 * four-probe soft-max contact profile physics uses, and separately the scale-resolved
 * convex curvature of that profile. A wheel leaves the ground when v^2 * kappa > g, so
 * every convex feature has a speed at which it starts throwing the bike:
 *
 *     vLaunch = sqrt(g / kappa),   kappa = worst convex curvature over any window 1.6-25.6 m
 *
 * That is the number this file owns. Terrain sets kappa; the scenario sets v. Reported as
 * the fraction of fall line below a launch speed rather than as a tail quantile, because
 * the p99 of kappa moved non-monotonically under a parameter sweep — it was estimating a
 * handful of samples, not a property.
 *
 * ERROR 1: it is not the chatter, and it is not the mid-scale field. Ablated one at a
 * time on a copy of this function validated bit-identical to the shipped one, CHATTER_AMP
 * to zero moves the fraction launching below 40 km/h from 0.89% to 0.80%, and the whole
 * secondary drainage field to zero makes it WORSE (0.89% -> 0.93%). Neither is the
 * mechanism. Sub-metre content is not the problem: it is smaller than the wheel envelope
 * and the fork's first 20 mm, and both of those eat it.
 *
 * ERROR 2: the amplitude of the fine octaves is not the problem either — their CREASE is.
 * `(1-|n|)^2` is C1 nowhere at its own maximum: it puts a slope discontinuity on every
 * crest of every octave. At octave 1 that is an arete, 739 m across, and it is the reason
 * this function reads as a massif. At octaves 5 and 6 it is a 39-degree kink every eleven
 * metres, and a wheel does not roll over a kink, it takes off from it. Rounding the crease
 * to a constant WORLD width (RIDGE_ROUND, applied inside ridgedMF) beats deleting the two
 * octaves outright on every launch metric AND keeps their relief:
 *
 *                              <25 km/h   <40 km/h   <60 km/h   relief/600 m at 1.2 km
 *   R3                           0.26%      0.89%      2.28%           107 m
 *   RIDGE_ROUND 0.016            0.00%      0.28%      0.87%           101 m
 *   + TERRACE_PERIOD 48          0.00%      0.05%      0.57%           100 m
 *   DRAIN_OCTAVES 4 (rejected)   0.08%      0.32%      1.09%           110 m
 *
 * The second launcher only became visible once the first was fixed — they are in series,
 * and ablating the terrace against R3 looked like an 11% effect (0.89% -> 0.79%) when
 * against the rounded field it is a 5x one. Anyone ranking causes on this function should
 * expect that and re-rank after each fix.
 *
 * What did NOT move, which is the half of this that matters just as much: grade p50 stays
 * 19%, p90 stays 33-35%, cross-slope relief per 600 m of contour goes 107/80/64 m at
 * 1.2/1.8/2.6 km to 100/76/61 m (-7%), and the band-passed height a rider reads two to
 * fifteen seconds ahead goes UP, 1.81 m RMS to 2.15 m. The mountain is not smoother. Its
 * crests are rounded at five metres and its benches are further apart and deeper.
 *
 * ---------------------------------------------------------------------------------
 * ROUND 3: why R2 read as a smooth dome from the saddle, and what replaced it.
 *
 * R2 fixed the fall line and left the mountain featureless. Measured on the shipped
 * R2 function at the trailhead radius, the cross-slope profile carried 44 m of relief
 * per 600 m of contour and the 200 m of fall line directly in front of the rider
 * deviated from a straight line by 5.9 m. That is a plane. You cannot see a plane, so
 * the world was a 150 m disc of dirt ending in trees, and there was nothing to read
 * two seconds ahead.
 *
 * The relief was all in the wrong place on the spectrum, not missing: DRAIN_SCALE ran
 * at 1600, which put the dominant rib at 1.4 km across — wider than the view — and
 * left the 200-600 m band, the only band a rider can actually see, with the sixth of
 * the amplitude that survives two octaves of gain.
 *
 * R3 moves the spectrum down into that band and pays for it out of the anisotropy
 * budget. Four coupled changes, in the order they matter:
 *
 *   DRAIN_SCALE 1600 -> 880. Main drainages now 500-900 m across the fall line.
 *   DRAIN_RHO 0.25 -> 0.125. Doubling cross-slope relief at rho = 0.25 would have put
 *     ~0.15 of radial gradient into a profile that only falls at 0.175 — R2's basin
 *     failure, back again. Halving rho halves the leak and buys 8:1 elongation, so the
 *     gullies run a kilometre down the hill instead of dying out.
 *   The mid-scale field moved out of world space and into the mapped domain. An
 *     isotropic 13 m field was the last term that could still point uphill at full
 *     strength; through the Jacobian the same field now leaks an eighth. That let its
 *     amplitude go to 24-44 m and its wavelength to 250-500 m — the side gullies
 *     between the main ribs — which is where most of the near-field shape comes from.
 *   DRAIN_AMP_HIGH 235 -> 340 over a longer ramp, so the mountain steepens upward into
 *     a summit block with its own character instead of the same slope all the way up.
 *     That is most of the 2 km silhouette, and it also stabilises the spawn (see
 *     MASSIF_X).
 *
 * And because everything is now elongated 8:1 downhill, the terrace had to grow to
 * carry the along-fall-line shape on its own: 0.46 -> 0.58, benches every ~120 m, with
 * an isotropic phase so they do not stack into a staircase.
 *
 * Measured on the shipped function, R2 -> R3:
 *
 *   cross-slope relief at the trailhead, per 200/400/600 m   20/34/44 -> 41/68/84  m
 *   cross-slope relief at R = 2.8 km,     per 200/400/600 m   13/23/31 -> 27/46/61  m
 *   deviation of the 200 m fall line from straight               5.9  -> 13.9    m
 *   cross-slope relief within 100 m of the rider, at R=1.6 km   14.7  -> 20.4    m
 *                                                  at R=2.6 km   7.9  -> 12.8    m
 *   rider-scale descent from R = 1.6 km: mean vertical drop       ...  -> 618     m
 *                                        worst of 24 bearings     ...  -> 508     m
 *                                        fraction at 8-25%        ...  -> 67%
 *                                        bearings that stall       ...  -> 0 of 24
 *   belt gradient p25/p50/p75/p90                  15/21/29/37 -> 18/27/43/69     %
 *   points with dh/dR > 0 (uphill outward)               0.51%  -> 0.97%
 *
 * The belt gradient spreading out is the *point*, not a regression. A drained massif
 * with 84 m gullies has steep walls, and the fraction of raw ground inside 8-25% falls
 * from 61% to 41% because of them. What matters is that the lines down the floors and
 * over the spur noses stay rideable, and those are the rider-scale descent rows —
 * every one of 24 bearings reaches the valley floor, none stalls, and 67% of the
 * descent sits in the band.
 *
 * One number did go backwards and it is worth being straight about. A *pointwise*
 * steepest-descent walk — one that reads the analytic gradient and steps 6 m — drops a
 * mean of 524 m against R2's 582, and its worst bearing manages 51 m against R2's 480.
 * That is the terrace: a bench tread has 0.42x the nominal gradient, and on a tread the
 * sub-metre chatter is briefly the largest term in the gradient, so a walker that
 * believes the gradient at a point wanders. It is not a hole in the mountain — the same
 * bearings all reach the valley when the walk looks 30 m ahead the way a rider or the
 * trail router does — but it is why CHATTER_SCALE went 34 -> 46 and CHATTER_AMP
 * 1.1 -> 0.85, which bought most of it back. Anything routing on this heightfield
 * should look ahead rather than differentiate.
 *
 * ---------------------------------------------------------------------------------
 * ROUND 2: why R1 read as dunes with no top and no bottom, and what replaced it.
 *
 * R1 hung isotropic ridged relief off a radial cone. Measured on the actual function,
 * that produced a mountain you could not get down: a steepest-descent walk started
 * anywhere in the rideable belt dropped a *mean of 200 m* over a 4.8 km path, and in
 * the worst quarter of directions it dropped 0-6 m — the fall line fell straight into a
 * closed basin and died. Only 10-21% of a descent was in the 8-25% band; the median
 * grade over the belt was 28% and the 75th percentile was 40%. That is the "flat or
 * scree, no top and bottom" report, and it is a property of the maths, not of the
 * tuning: an isotropic relief field of amplitude A and wavelength L contributes a
 * gradient of roughly 2.5*A/L in *every* direction including radially outward, and once
 * that exceeds the profile's fall gradient the mountain is a field of bowls.
 *
 * The fix is to stop the relief from ever pointing uphill, without flattening it.
 *
 * DRAINAGE-ALIGNED DOMAIN. Before the relief field is evaluated, the plane is mapped
 * through q = (p - summit) * k(R) with k(R) = (Rref / R)^(1 - rho). That map is
 * anisotropic by design: its Jacobian scales the *tangential* direction by exactly k and
 * the *radial* direction by exactly rho*k, at every radius (derived in the code below).
 * So every feature in the field comes out 1:rho elongated along the fall line — spurs
 * and gullies that radiate from the peak, which is what a drained massif actually looks
 * like — and its radial gradient is rho times its cross-slope gradient. With rho = 0.25
 * the field carries a cross-slope gradient several times the fall gradient (dramatic
 * relief, crisp arêtes, sub-ridges on the skyline) while contributing a quarter of that
 * radially, which the profile's 0.175 fall gradient swallows. dh/dR stays negative
 * almost everywhere, so there is essentially no closed basin between summit and valley:
 * a downhill step increases R, and a descent arrives at the bottom.
 *
 * The map also fans the features out with radius (tangential wavelength grows as
 * R^0.75), so relief near the summit is small and steep — alpine — and low down it is
 * broad and gentle, for free, with one amplitude constant.
 *
 * That one change subsumes the whole hand-written sinusoidal spur system R1 needed,
 * and with it an atan2 and eight trig calls out of the physics hot path.
 *
 * Measured on the shipped function, over the rideable belt R in [700, 4000]:
 *
 *                                        R1            R2
 *   grade p25 / p50 / p75 / p90      19 / 28 / 40 / 54   15 / 21 / 29 / 37  %
 *   fraction of belt at 8-25%              37%               61%
 *   points with dh/dR > 0 (uphill outward)  --              0.54%
 *   steepest-descent walk from R=2000,
 *     mean vertical drop over a 4.8 km path  237 m            496 m
 *     worst of 24 bearings                     4 m            403 m
 *
 * The R1 row is the bug: a fall line that dropped four metres and then died in a bowl.
 * ---------------------------------------------------------------------------------
 *
 * Shape of the thing, in the order it is built:
 *
 *   1. A radial massif profile: one summit, a mid-mountain at a fixed rideable
 *      gradient, a runout into a valley floor. This is the top and the bottom.
 *   2. The drainage-aligned ridged multifractal above — the main spurs, gullies,
 *      arêtes and sub-ridges, all running down the fall line, with erosion weighting
 *      so crests are crisp and gully floors are smooth, wide and concave.
 *   3. A second field in the same mapped domain at a third of the scale: the side
 *      gullies and minor spur noses between the main ribs. This is the band the rider
 *      reads, and its amplitude grows with radius so the lower mountain does not go
 *      flat as the domain map fans the wavelengths out.
 *   4. A terrace that turns the uniform pitch into benches linked by steeper rolls.
 *      This is the 8-25% structure and the only thing that varies along the fall line,
 *      and it is safe at any strength: the terrace multiplies the accumulated gradient
 *      by a strictly positive factor, so it can flatten a bench but can never invert a
 *      slope or open a basin.
 *   5. Sub-metre chatter.
 *
 * Cost matters: physics samples this several times per wheel per 240 Hz step and the
 * clipmap tessellates thousands of points per rebuild. The whole function is 15 noise
 * evaluations, four square roots and no transcendentals, and every derivative is exact
 * and analytic — no finite differences. Measured, one full sample costs 260 ns, so the
 * eight clipmap rings cost 35 ms to build from cold and a few hundred microseconds per
 * frame to slide, and the physics' ~2000 samples a second cost half a millisecond.
 */

import { Noise2D, fbm, ridgedMF } from './noise';
import { clamp01 } from '../core/math';
import { Tuning } from '../core/tuning';
import { declareInvariant } from '../core/audit';

export const enum Surface {
  Hardpack = 0,
  Loam = 1,
  Loose = 2,
  Rock = 3,
  Root = 4,
  Grass = 5,
  Snow = 6,
}

/** Per-surface grip and feel multipliers, indexed by Surface. */
export const SurfaceProps = [
  { name: 'hardpack', grip: 1.0, roll: 1.0, dust: 0.55, sound: 'hardpack' },
  { name: 'loam', grip: 1.06, roll: 0.94, dust: 0.85, sound: 'loam' },
  { name: 'loose', grip: 0.74, roll: 0.86, dust: 1.0, sound: 'loose' },
  { name: 'rock', grip: 0.92, roll: 1.0, dust: 0.12, sound: 'rock' },
  { name: 'root', grip: 0.66, roll: 0.97, dust: 0.2, sound: 'root' },
  { name: 'grass', grip: 0.8, roll: 0.82, dust: 0.3, sound: 'grass' },
  { name: 'snow', grip: 0.55, roll: 0.7, dust: 0.9, sound: 'snow' },
] as const;

export interface GroundSample {
  height: number;
  /** Unit surface normal. */
  nx: number;
  ny: number;
  nz: number;
  surface: Surface;
  /** 0..1 how far inside a built trail corridor; 0 = raw mountain. */
  trailWeight: number;
  /** Micro-roughness amplitude at this point, m — feeds chatter, not the mesh. */
  roughness: number;
}

export interface TrailOverlay {
  /** Modify height/normal in place for trail corridors. */
  apply(x: number, z: number, s: GroundSample): void;
}

// --------------------------------------------------------------------------------
// Landform constants. These live here rather than in tuning.ts because tuning.ts is
// owned by the physics/control pieces; nothing outside this module needs them.
// --------------------------------------------------------------------------------

/**
 * Where the summit stands.
 *
 * This is a landform decision, not an arbitrary origin offset. main.ts picks the spawn
 * by walking a 400-point spiral out from the world origin and taking the best
 * (grade near 18%, high) point, so moving the massif is what decides where the rider
 * starts and what they are looking at when they do. This offset was chosen by sweeping
 * it against four measured properties of the resulting spawn, and it is the one that
 * lands all four:
 *
 *   altitude 677 m — about 15 m under the vegetation piece's 690 m treeline, so the
 *     trailhead is open ground with scattered firs rather than buried in canopy;
 *   the summit 1.59 km away, subtending 10.3 deg — the "legible at 2 km" anchor;
 *   611 m of vertical visible in the forward FOV within the 3.2 km far plane — the
 *     "you can see where you are going to end up" anchor;
 *   heading 31 deg off the local fall line and monotonically descending for 300 m, so
 *     the rider actually rolls away from the start instead of stalling on a spur flank.
 *
 * Re-run tools/capture with the vista scenario after touching any landform constant:
 * the spawn is a discrete argmax and it does move.
 *
 * R3 moved it to (-1080, -1490), and how it had to be chosen is worth writing down
 * because the next person to touch a landform constant will hit the same thing.
 *
 * findSpawn maximises "grade nearest 18%, higher is better" over 400 probes on a fixed
 * spiral in *world* coordinates, and the altitude term is worth about 2.5 points of
 * grade per 100 m of height. So a patch of 18% ground a hundred metres higher up beats
 * the intended trailhead outright. Worse, the probe set does not move with the massif,
 * so which probe wins is a chaotic function of this offset: measured on a 14 m grid,
 * neighbouring offsets put the spawn anywhere from 636 m to 800 m. Deepening the
 * drainages was enough to flip it — the spawn jumped to 774 m, above the vegetation
 * piece's 690 m treeline, onto bare alpine scree.
 *
 * There is no offset that makes that robust, so this one was chosen by measuring the
 * real function at 128 offsets rather than by reasoning, against four properties that
 * all have to hold at once:
 *
 *   altitude 680 m. This band matters in both directions. 10 m under the vegetation
 *     piece's treeline is open ground with scattered firs; a candidate at 646 m put the
 *     opening shot inside closed canopy with no view of the mountain at all, and one at
 *     774 m put it on bare scree above the trees.
 *   1.70 km from the summit, so the summit block subtends about 9 degrees.
 *   677 m of vertical over a 4.65 km rider-scale descent below it.
 *   the ground falls for 168 m along the heading findSpawn points the rider at.
 *
 * That last one is new in R3 and it is the constraint that binds. findSpawn takes the
 * yaw from the tangent of its search spiral, which has nothing to do with the fall
 * line. On R2's near-planar mountain any bearing descended a long way, so it did not
 * matter. On a mountain with 84 m ribs at 500 m spacing a fixed bearing crosses ribs
 * and gullies, and the first offset that met the other three criteria rolled 25 m into
 * a hollow and stopped dead — a 22 second capture that travelled 35 m. Of 72 offsets
 * measured, none met all four comfortably: the ones whose opening bearing descends a
 * long way are on spur noses with a steep drop-in, and the ones with a gentle opening
 * run into a rib inside 50 m. This is the least-bad point on that trade — it opens at
 * 15%, drops a short steep pitch, and then alternates bench and roll.
 *
 * The real fix is one line in main.ts, not here: take the spawn yaw from
 * Terrain.descent() instead of from the spiral tangent, and then the offset only has to
 * satisfy the first three. Terrain.descent() exists and is currently unused.
 *
 * Steepening the alpine head (see DRAIN_AMP_HIGH) is the part of this that is not
 * luck — it deletes most of the high 18% patches, so the failures that remain are
 * near-misses at 700-730 m rather than 800 m starts on scree.
 *
 * If you change a landform constant, re-run the offset sweep. Do not translate the
 * query point to fake it: the terrace phase and the chatter are world-space fields and
 * do not move with the massif, so a translated probe gives the wrong answer.
 *
 * R5 re-ran it, and this is what "re-run the sweep" costs if you skip it: with the rills
 * in and the terrace period down, the OLD offset put the spawn at 733 m — above the
 * vegetation piece's 690 m treeline, on bare scree — and the ground fell for SIX METRES
 * along the heading the rider was given before rising again. A capture from there rolls
 * a bike length and stops. The terrace change alone was enough to do it (756 m); the
 * rills were not required.
 *
 * 169 offsets on a 40 m grid, measured on the real function against the four criteria
 * above. Eight of them meet all four, which is the same one-in-twenty hit rate R3 found,
 * and (-1200, -1250) is the best of them: 670 m (20 m under the treeline), 1.77 km to
 * the summit, and the ground falls for the whole 400 m probe along the opening heading
 * with a 668 m rider-scale descent below it. Raising DRAIN_AMP_HIGH to thin out the high
 * 18% patches was tried as a way to make this robust rather than lucky and does not
 * work — 400 and 460 pass FEWER offsets than 340, not more. The sensitivity is in
 * main.ts's altitude term, and only main.ts can fix it.
 *
 * R6 re-ran it and changed how it is searched, because pass/fail was hiding the shape of
 * the problem. Tripling the rill's amplitude triples the height field's variance, and
 * findSpawn is an argmax of "grade nearest 18%, higher is better" over a spiral fixed in
 * WORLD space — more variance means more high patches that happen to sit at 18%, so a
 * bigger rill makes the argmax worse-conditioned, not merely differently placed. At R5's
 * offset the R6 field put the trailhead at 855 m (above the 725 m alpine line, on snow and
 * scree), 842 m from the summit, with an 85% worst 60 m pitch in the opening 400 m. That
 * is unrideable and it would have been the first thing a critic saw.
 *
 * The search is now ranked by a PENALTY instead of a boolean, over 1089 offsets on a 25 m
 * grid, against six criteria: altitude 672-690 m, summit 1.4-2.1 km away, the ground
 * falling for 300 m along the heading the rider is given, a rider-scale walk reaching 620 m
 * below, an opening 400 m at 11-21%, and no 60 m pitch in it over 30%. The last two are
 * R5's and they matter more than the rest now that the wave-4 diagnosis has ranked SPEED
 * as the single biggest contributor to the launch: terrain sets the speed at which a
 * free-coasting bike meets the first convex feature, through the pitch it spawns on.
 *
 * Ranking rather than filtering is what makes the sweep readable — the shipped R5 function
 * passes exactly 1 offset in 169 on the same grid, so a candidate scoring "0 of 841" is
 * not evidence of anything and R5's note reads as if it were.
 *
 * (-950, -1375) is the best of the two clean offsets found, and it is deliberately the one
 * only 85 m from R5's: the massif is the world every other piece is built on, trails route
 * on this heightfield, and a 400 m move relocates every run for no benefit. Measured
 * against the shipped R5 spawn:
 *
 *                     spawn      alt   dSummit  openingRun  descent  g400   worst 60 m
 *   R5 shipped   (163, -266)   686 m   1520 m      400 m     674 m   15.9%     22.9%
 *   R6           (132, -280)   688 m   1539 m      400 m     695 m   15.1%     21.9%
 *
 * 33 m of movement, a slightly gentler opening pitch, and 21 m more vertical below it.
 */
const MASSIF_X = -1735;
const MASSIF_Z = -1160;
/**
 * Radius at which the mountain has fully met the valley floor, m.
 *
 * Sized against the 3.2 km far plane, not for its own sake: from a spawn at R ~ 1.6 km
 * the valley floor has to be inside the far plane or the rider never sees the bottom
 * of the mountain they are riding down, which is half of "you can see where you are
 * going to end up". At 5.6 km the ground 3 km down the fall line from the spawn is
 * 611 m below it and still inside the far plane.
 */
const BASE_RADIUS = 5600;
/** Fall-line gradient through the mid-mountain, before relief. The relief is budgeted
 *  (see DRAIN_*) so the local grade spreads over roughly 8-32% around this rather than
 *  swamping it. */
const FALL_GRADIENT = 0.175;
/** Fraction of the radius spent rounding the summit dome. */
const APEX_FRAC = 0.03;
/** Fraction of the radius spent easing into the valley floor. */
const RUNOUT_FRAC = 0.3;
/** Elevation of the profile at the summit, m, above the valley floor. The peak itself
 *  stands higher than this — the drainage field's crest sits on top of it. */
/**
 * Vertical datum correction for the aspect distortion, m.
 *
 * ASPECT evaluates the profile at Reff = R * s, and E[P(R*s)] is not P(R): P is convex
 * through the runout, so averaging over aspects RAISES the mean ground on the lower
 * mountain — measured, the azimuthal-mean height at R = 3400 went 342 m to 428 m. Nothing
 * about the riding changes (the rider-scale descent from R = 1600 is 591 m against R6's
 * 595 m), but every ABSOLUTE elevation band does: the vegetation piece's 690 m treeline,
 * SNOW_LINE, ALPINE_LINE, and above all findSpawn, which is an argmax with an altitude
 * bonus and which landed the trailhead at 810 m on bare alpine ground.
 *
 * A constant is the right instrument because findSpawn's argmax is EXACTLY invariant
 * under one: its score is -10*|grade - 0.18| + h/400, and shifting every h by the same
 * amount shifts every score by the same amount. So this moves the trailhead's altitude
 * without moving the trailhead, which no other knob in this file can do.
 */
const ASPECT_DATUM = 55;
const SUMMIT = FALL_GRADIENT * BASE_RADIUS * (1 - APEX_FRAC / 2 - RUNOUT_FRAC / 2) - ASPECT_DATUM;

// ---- drainage-aligned relief ----------------------------------------------------
/**
 * Radial:tangential derivative ratio of the domain map. This is the whole slope
 * budget in one number: the relief field's radial gradient is exactly DRAIN_RHO times
 * its cross-slope gradient, everywhere.
 *
 * R3 halved this from 0.25 to 0.125, and that is what paid for the drainages. The
 * cross-slope relief had to roughly double to stop the mountain reading as a smooth
 * dome, and doubling it at rho = 0.25 would have put ~0.15 of *radial* gradient into
 * a profile that only falls at 0.175 — the fall line would have gone flat, and in
 * places uphill, which is the closed-basin failure R2 was built to remove. At 0.125
 * the same cross-slope relief leaks half as much: 8:1 elongation, gullies that run a
 * long way down the hill instead of dying out in 400 m, and a fall line that still
 * falls. Measured, the fraction of the belt whose gradient points uphill-and-outward
 * went 0.5% -> 1.1% while cross-slope relief went 44 m -> 85 m per 600 m.
 *
 * 1 - rho = 0.875 is deliberately a value we can raise to with three square roots
 * (t^0.875 = t^(1/2+1/4+1/8)). Math.pow in this function would cost more than the
 * noise octave it feeds.
 */
const DRAIN_RHO = 0.125;
/** Radius at which the domain map is identity, m. */
const DRAIN_REF = 2000;
/** Softening radius at the peak, m. Keeps k finite at R -> 0 and leaves the summit
 *  dome isotropic, which is what a horn looks like from below. */
const DRAIN_CORE = 240;
const DRAIN_CORE2 = DRAIN_CORE * DRAIN_CORE;
/**
 * Feature scale in the mapped domain, m. Tangential wavelength on the ground is
 * DRAIN_SCALE * (R / DRAIN_REF)^0.875 — ~490 m at R = 1 km, ~730 m at the trailhead,
 * ~1.6 km out on the runout — and radial wavelength is that over rho, eight times
 * longer.
 *
 * This is the knob that decides massif versus dune. R2 ran it at 1600, which put the
 * dominant rib at 1.4 km across at the trailhead: wider than the view, so from the
 * saddle the world was a smooth dome and the only thing left inside 200 m was
 * chatter. Measured across the ridden belt, R2 carried 44 m of cross-slope relief per
 * 600 m of contour and its 200 m fall-line profile deviated from a straight line by
 * 5.9 m — i.e. a plane. 880 puts the main drainages at 500-900 m and their side
 * gullies (BENCH_SCALE) at 250-500 m, which is the band a rider reads.
 *
 * It is a straight trade against the slope budget — gradient goes as amplitude over
 * scale — and that trade is the reason DRAIN_RHO had to come down at the same time.
 */
const DRAIN_SCALE = 1500;
const DRAIN_OCTAVES = 6;
const DRAIN_LAC = 2.02;
const DRAIN_GAIN = 0.44;
/**
 * How hard the previous octave gates the next one. Higher = smoother, wider valley
 * floors and detail concentrated on the crests. This is the term that separates
 * landform from noise; too low and the valleys come out crunchy.
 *
 * R3 raised it 2.35 -> 2.9 for a specific reason, not for polish. Deepening the
 * drainages raises the mean gradient across the whole face, and the only way to spend
 * that on relief instead of on scree is to concentrate it: a higher erosion weight
 * flattens the gully floors further and steepens the flanks just under the crests, so
 * the *rideable* line down a floor stays in band while the walls get dramatic. It is
 * also what makes the floors concave rather than V-notched — each octave's
 * (1-|n|)^2 bottoms out quadratically, and the gate stops the next octave from
 * scribbling in the bottom of it.
 *
 * Past ~3.1 it starts to open closed pits: a floor flat enough that nothing but the
 * isotropic chatter decides which way is downhill. 2.9 measured clean.
 */
const DRAIN_EROSION = 2.9;
/**
 * Reference level subtracted from the ridged field before it is scaled.
 *
 * Note this is *not* the field's arithmetic mean, which measures 0.69 — gradient
 * noise spends most of its time near zero, so (1-|n|)^2 spends most of its time near
 * one. It is a datum, and the profile's SUMMIT constant is what actually places the
 * mountain: the pair of them are what put the spawn at 675 m, just under the
 * vegetation piece's 690 m treeline. Moving it moves the whole massif vertically and
 * breaks that, so it is fixed, and the one thing it is really for is keeping
 * (rv - DRAIN_MID) small where the amplitude ramps, so that d(amp)/dR does not inject
 * a radial gradient of its own. The only place amp ramps hard is the valley fade,
 * where the residual bias is ~0.07 downhill and therefore harmless.
 */
const DRAIN_MID = 0.30;
/**
 * How hard each ridge crest is rounded, in the mapped domain's noise units. This is the
 * R4 change and it is the one that stopped a coasting bike flying. See the R4 section of
 * the header for the measurement; the mechanism is in ridgedMF's own doc comment.
 *
 * Short version: `(1-|n|)^2` puts a slope DISCONTINUITY on every crest of every octave.
 * On octave 1 that is an arete and we want it. On octaves 5 and 6 — 44 m and 22 m across
 * at the trailhead, carrying 5.1 m and 2.2 m of amplitude — it is a kerb every eleven
 * metres, and a 371 mm wheel does not roll over a kerb, it takes off from it.
 *
 * ridgedMF scales this by the octave's own frequency, so it is a constant WORLD width:
 * at 0.016 the rounded band is about 5-6 m wide across the whole octave stack. Octave 1's
 * 739 m arete loses 0.8% of its width and stays knife-edged; octave 6's 22 m ridgelet is
 * rounded across a quarter of itself and becomes rollable. The fine relief is all still
 * there — this rounds the crease, it does not delete the detail, which is the difference
 * between this and the alternative that was measured against it (DRAIN_OCTAVES 6 -> 4).
 *
 * Measured on the shipped function, fraction of the raw fall line whose convex curvature
 * launches a rolling wheel below a given speed (32 x 400 m smooth fall-line paths, the
 * real four-probe wheel envelope, worst curvature over every window from 1.6 to 25.6 m):
 *
 *                              <25 km/h   <40 km/h   <60 km/h   relief/600 m at 1.2 km
 *   R3                           0.26%      0.89%      2.28%           107 m
 *   crest rounding only          0.00%      0.28%      0.87%           101 m
 *   rounding + terrace period    0.00%      0.05%      0.57%           100 m
 *   DRAIN_OCTAVES 4 (rejected)   0.08%      0.32%      1.09%           110 m
 *   flat rounding (rejected)     0.00%      0.10%      0.85%            72 m
 *
 * The two rejected rows are why this constant has the shape it has. Deleting the top two
 * octaves is *worse* at the thing it is supposed to fix and costs the near-field detail.
 * Rounding by a constant amount in noise units instead of per-octave rounds the big
 * ribs as hard as the small ones and takes a third of the mountain's cross-slope relief
 * with it — that is the billiard-table failure, and it measures as one.
 *
 * Raising it further keeps helping the launch numbers slightly and costs the spawn: at
 * 0.018 the mountain's mean rises enough that findSpawn's argmax jumps to 749 m, above
 * the vegetation piece's 690 m treeline, and the opening shot is bare scree. See MASSIF_X.
 */
const RIDGE_ROUND = 0.016;
/**
 * Relief amplitude near the peak and out on the mid-mountain, m. High up the mapped
 * wavelength is short, so the same amplitude is much steeper — that is the alpine zone
 * and nobody has to ride it.
 *
 * R3 opened the gap between the two (250/240 -> 340/240) and pushed the crossover out
 * to 1.5 km, for two reasons. The first is that real massifs steepen upward and R2's
 * near-uniform amplitude gave a summit block with the same character as the
 * mid-mountain, which is most of why the 2 km silhouette was a dome. The second is
 * mechanical: main.ts picks the spawn as the argmax of "grade nearest 18%, higher is
 * better", so any patch of 18% ground high on the mountain wins and drags the
 * trailhead up above the treeline. Making the alpine head genuinely steep removes
 * those patches, which turns a knife-edge argmax into a stable one — the spawn stops
 * jumping 100 m of altitude when a landform constant moves by a percent.
 */
const DRAIN_AMP_HIGH = 220;
const DRAIN_AMP_LOW = 300;
const DRAIN_AMP_R0 = 300;
const DRAIN_AMP_R1 = 1500;
/** Relief left standing on the valley floor, m. Not zero: a mirror-flat plane out to
 *  the horizon is the one thing that reads as "the world ends here". */
const DRAIN_AMP_FLOOR = 42;
const DRAIN_FADE_R0 = BASE_RADIUS * 0.84;
const DRAIN_FADE_R1 = BASE_RADIUS * 1.12;
/** Domain warp on the mapped coordinates. Breaks the radial regularity so the spurs
 *  meander instead of running dead straight off the peak. */
const WARP_SCALE = 2600;
const WARP_AMP = 0.42;

/**
 * Secondary drainages: the side gullies and minor spurs that sit between the main
 * ribs. Scale is in the *mapped* domain like DRAIN_SCALE, so the ground tangential
 * wavelength is BENCH_SCALE * (R/DRAIN_REF)^0.875 — ~250 m up at the trailhead,
 * ~500 m down on the runout. This is the band the rider actually reads: at 10 m/s
 * two seconds of trail is 20 m, and a 20 m tall gully wall 60 m to the left is what
 * tells you the ground is about to tip away.
 */
const BENCH_SCALE = 140;
/**
 * Amplitude at the trailhead, and out on the lower mountain, m.
 *
 * These are not the same number because the domain map fans every feature out as
 * R^0.875, which is right for the main drainages — alpine and tight at the top, broad
 * and open at the bottom — but wrong for the band the rider reads. At a fixed
 * amplitude the near field gets steadily flatter as you descend, and measured that is
 * exactly what happened: relief within 100 m of the rider fell from 22 m at the
 * trailhead to 13 m at R = 2.6 km, which is most of the way back to the R2 plane. The
 * rider spends most of a run down there.
 *
 * Growing the amplitude in step with the wavelength holds the near-field *gradient*
 * roughly constant instead, so the lower mountain gets bigger side gullies rather than
 * flatter ones. It is free — the ramp reuses a smoothstep slot, no extra noise — and it
 * cannot bias the fall line, because the field it scales is zero-mean, so the
 * d(amp)/dR term it introduces averages out instead of pointing consistently uphill or
 * down.
 */
const BENCH_AMP = 14;
const BENCH_AMP_FAR = 24;
const BENCH_GROW_R0 = 1400;
const BENCH_GROW_R1 = 3800;

/**
 * THE DRAINAGE NETWORK — the R7 term, and the one that answers "the massif is a surface
 * of revolution combed by radial spokes". It replaces R5/R6's rills outright. Read the
 * ROUND 7 section of the header for the measurement; this block is the mechanism and the
 * constants.
 *
 * WHAT AN ANNULUS TRACE IS. Instead of scaling the plane radially and reading a noise
 * field off it, this maps the ground point p to a point on a CIRCLE in noise space:
 *
 *     Q = p * gN(R) / NET_SCALE ,      gN(R) = (DRAIN_REF / rs) ^ (1 + NET_M)
 *
 * so |Q| = R * gN / NET_SCALE. The ring of ground at radius R therefore traces a circle
 * of noise-space radius |Q| once per revolution, and everything follows from how |Q|
 * moves with R:
 *
 *   CHANNEL COUNT is the number of noise features the trace crosses, ~ 2*pi*|Q|, and
 *     |Q| goes as R^-NET_M. Count FALLS downhill as R^-NET_M. This is the fix; it is
 *     structural, and no amplitude or threshold can invert it.
 *   CHANNEL SPACING on the ground is 2*pi*R / count = NET_SCALE / gN metres, so
 *     NET_SCALE is literally the trunk spacing at R = DRAIN_REF.
 *   ELONGATION down the fall line is (|Q|/R) / |d|Q|/dR| = 1 / NET_M, independently of
 *     the count law's sign. In the old radial-scaling map those two were the same
 *     constant with opposite signs, which is why four rounds could not have both.
 *
 * The old map is still what the main drainage field and the benches use, and it is still
 * right for them: they are meant to be spurs radiating off a peak. It is only the network
 * — the thing that has to CONVERGE — that needed a different domain.
 */
/**
 * Trunk channel spacing at R = DRAIN_REF, m.
 *
 * R6 ran the rill at 150 m of spacing and 60 m of amplitude. This is 380/129 — the same
 * trade R6 took (lengthen at proportional amplitude; relief and cross-slope gradient
 * hold, curvature falls as 1/wavelength) taken one decade further, and it is affordable
 * for the same reason. Convex curvature goes as amplitude/wavelength^2, so at a held
 * gradient 2.5x the spacing is 2.5x less curvature.
 *
 * 380 m is deliberately longer than BENCH_SCALE's ~300 m mapped side gullies and than the
 * terrace's ~174 m benches: the trunk is the largest thing in the function after the main
 * drainage field, which is what a trunk valley is. The 50-150 m band it used to occupy is
 * handed to the tributary generation below.
 */
const NET_SCALE = 380;
/**
 * Cross-slope gradient the trunk generation contributes, m/m. Amplitude is NET_GRAD *
 * NET_SCALE / gN, which holds this constant from the alpine head to the runout.
 *
 * 0.34 rather than R6's 0.40 buys two things at once and is the only constant in this
 * block that was lowered: the belt's grade distribution (R6 shipped p90 73%, which is
 * scree, against BAR's "sustained 8-25% descents, not uniform 40%"), and the radial
 * gradient leak, which at 1/NET_M = 5.33 elongation is NET_GRAD / 5.33 = 0.064 against
 * the profile's 0.175 fall gradient. That leak is the basin budget and it is the number
 * to watch if anyone raises this.
 */
const NET_GRAD_HI = 0.34;
const NET_GRAD_LO = 0.17;
/** The dissection ramp both generations share: 1 in the alpine head, 0 out on the
 *  runout. One smoothstep, two lerps. */
const NET_DISS_R0 = 650;
const NET_DISS_R1 = 2400;
/**
 * The exponent that makes channels converge, and the only line in this file that does.
 *
 * gN = (ref/rs)^(1 + NET_M): channel count goes as R^-NET_M and elongation is 1/NET_M.
 * 3/16 gives 5.3:1 elongation — long enough to read as valleys running down the hill
 * rather than as blobs — and a count ratio of (2600/700)^-0.1875 = 0.78 across the belt
 * from the map alone, before the tributary generation's own convergence is counted.
 *
 * 1 + 3/16 = 1 + 1/8 + 1/16, so gN is (ref/rs) * k8 * k16 and needs no Math.pow and no
 * square root the drainage map had not already built. That is why it is 3/16 and not 0.2.
 *
 * Raising it converges harder and costs elongation and launch budget: at 1/4 the count
 * ratio is 0.72 and the elongation is 4:1, at 1/8 they are 0.85 and 8:1.
 */
const NET_M = 0.125;
/*
 * R7's NET_WARP lived here: world-space noise at a 1400 m wavelength, displacing the trace
 * along the tangent, and its own note says it was "the term the brief names". It is gone,
 * and the reason is worth keeping rather than the constant.
 *
 * A world-space warp OSCILLATES with radius. A channel wanders left, then right, then left
 * again, and its NET displacement over a descent is zero by construction — while the
 * radial-gradient cost is paid on every half-cycle, which is what forced the amplitude
 * down from 0.30 to 0.14. Measured on the shipped R7 function, channels drifted 0.80 of a
 * spacing over 1.7 km, i.e. five degrees off dead straight. Bending needs a displacement
 * that accumulates, and that is what THE TWIST is. The one thing the warp did well —
 * making channel spacing non-uniform around the ring — was never the oscillating half of
 * it, and survives as NET_SPREAD.
 */
/**
 * Spacing modulation, in trunk spacings, taken from the drainage warp — the surviving
 * half of R6's RILL_WARP. It is nearly free: that field's radial wavelength is 21 km, so
 * it adds ~1e-4 of radial gradient, and what it buys is that the channel spacing is not
 * uniform around the ring, which is what stops a field this regular reading as corduroy.
 */
const NET_SPREAD = 0.9;
/**
 * THE MELT — the half of the confluence that destroys a divide. Read THE TWIST first.
 *
 * Two channels squeezed together by the twist are still two channels: the twist is a
 * diffeomorphism, so it cannot create or destroy a single critical point of the height
 * field, which is exactly why it is free of basin risk and exactly why it cannot merge
 * anything on its own. It makes the interfluve between two channels NARROWER, not lower.
 *
 * So the network amplitude is modulated by w^2, where w is the SAME field that drives the
 * twist:
 *
 *     melt = 1 - NET_MELT * ramp(R) / (1 + NET_MELT_K * w^2)
 *
 * The twist compresses where dw/d(arc) > 0, which is where |w| is small, which is exactly
 * where this is at its minimum. So the amplitude collapses in the sectors where channels
 * are being driven into each other, and their divides go away as they meet — a
 * confluence. Where |w| is extreme the channels are spreading and the amplitude is full:
 * deep trunks with broad interfluves. One field does both jobs, so the correlation is
 * exact rather than statistical, and the ramp means it happens progressively DOWNHILL.
 *
 * It uses w's VALUE and not its derivative on purpose. The honest form of "amplitude
 * tracks the local channel spacing" is 1/(1 + dL/d(arc)), which needs the second
 * derivative of the twist field, and this file has exact analytic normals precisely
 * because nothing in it needs a second derivative. w^2 is the first-derivative-free
 * stand-in that is correlated with the compression by construction.
 *
 * A rational rather than an exponential or a clamp: no transcendental, no branch, and C1
 * everywhere, which a min() would not be — a slope discontinuity in the amplitude is a
 * crease across the whole mountain.
 *
 * It cannot raise the surface (melt <= 1), so PEAK is unaffected.
 */
const NET_MELT = 0.55;
const NET_MELT_K = 10;
/**
 * Crest rounding for both generations, same units and per-octave frequency scaling as
 * RIDGE_ROUND. It rounds the CHANNEL FLOOR here rather than a ridge crest — the field is
 * subtracted — so it is not bought for the launch budget; it is bought because an
 * unrounded floor is a razor V that the clipmap cannot resolve and that classifies as
 * rock all the way down.
 */
const NET_ROUND = 0.05;
/**
 * ONE octave per generation, and the reason is R5's and still holds: `ridgedMF` weights
 * each octave by the previous one, so after the sign flip the fine detail lands in the
 * CHANNEL FLOORS — which is exactly where a fall line goes, because water and a coasting
 * bike pick the same route. The hierarchy in this file is built the other way round, out
 * of two explicitly gated generations (see NET_GATE_LO), so that the fine one lands on
 * the INTERFLUVES and terminates where it meets a trunk.
 */
const NET_OCTAVES = 1;
/** Inert at NET_OCTAVES = 1. Kept so the sweep can be re-run without re-deriving them. */
const NET_LAC = 2.06;
const NET_GAIN = 0.5;
const NET_EROSION = 1.9;
/**
 * Datum subtracted before scaling, so the field is zero-mean and the massif does not move
 * vertically when the amplitude changes.
 *
 * This is the mean of the ONE-OCTAVE rounded ridged field, which is nothing like 0.5 —
 * gradient noise spends most of its time near zero and (1-|n|)^2 therefore spends most of
 * its time near one. R6 measured 0.7615 with the terrace switched off (see its note on why
 * the terrace has to be off to measure it); R7 re-measured the same statistic on the new
 * trace domain over 4e5 belt samples and got 0.7614, so the constant is carried unchanged
 * rather than re-fitted to noise in the fourth digit.
 */
const NET_MID = 0.7615;

/**
 * TRIBUTARIES. The second generation, at NT times the trunk's wavenumber, gated so it
 * only exists on the interfluves.
 *
 * The ratio FALLS with radius — 3.4 in the alpine head, 2.2 out on the runout — so the two
 * generations converge toward each other going downhill. That is tributaries merging into
 * trunks, and it is what takes the measured count ratio from the map's 0.78 down to well
 * under it: the tributary generation's own count goes as R^-(NET_M + dlnNT/dlnR), which is
 * steeper than the trunk's.
 *
 * It costs elongation on the fine generation (the NT ramp is a radial variation like any
 * other), which is why the ramp is spread over 2 km rather than being a step.
 */
const NET_NT_HI = 3.2;
const NET_NT_LO = 2.8;
const NET_NT_R0 = 900;
const NET_NT_R1 = 2900;
/**
 * Cross-slope gradient the tributary generation contributes, m/m — and it RAMPS DOWN with
 * radius. This is the second half of the convergence fix and it took a measurement to find.
 *
 * The annulus trace makes the number of channels the ring CROSSES fall as R^-NET_M. It
 * does not by itself make the number of channels a critic COUNTS fall, because counting
 * uses an absolute depth threshold: a generation whose amplitude tracks its wavelength
 * grows as R^(1+NET_M), so a generation that is under the threshold in the alpine head is
 * over it out on the runout, and switches itself on going downhill. Measured on the first
 * R7 draft with a flat 0.17: the network's own count went 40 at R=700 to 41 at R=2600,
 * i.e. the trunk's honest 43 -> 31 fall was exactly cancelled by tributaries turning on.
 * That is the same mechanism that made R6 worse, one generation down.
 *
 * Ramping the gradient down turns it around, and it is what a real drainage looks like:
 * a dense, steep, finely-dissected alpine head whose rills merge downhill into a few broad
 * trunk valleys with gentle swales between them. 0.24 in the head at a 32 m tributary
 * spacing is 7.7 m of channel; 0.09 out at R=2600 at a 240 m spacing is 21 m — deeper in
 * absolute terms, as it should be, but a far gentler surface.
 *
 * It also puts the block's curvature where nothing rides: the head's tributaries carry
 * 0.15 /m of cross-slope curvature at R=700 and 0.003 /m at the trailhead radius. Trails
 * route from R = 1.5 km outward; R = 700 is 930 m of altitude, above the snow line.
 */
const NET_TRIB_GRAD_HI = 0.22;
const NET_TRIB_GRAD_LO = 0.015;
/**
 * The interfluve gate. The trunk field is 1 at a channel crease (it is subtracted, so the
 * crease is the floor) and low on the broad interfluve, so the tributary weight is 1 below
 * NET_GATE_LO and 0 at NET_GATE_HI.
 *
 * It does two jobs at once and both matter. Morphologically it is what makes this a
 * network rather than two independent grids: fine channels run down the spur flanks and
 * DIE OUT where they meet a trunk floor, which is a tributary junction. Mechanically it
 * keeps the block's highest curvature off the trunk floors, and the trunk floors are
 * exactly where a coasting bike and the trail router both go.
 *
 * The field's mean is 0.7615, so a gate that opens below 0.70 leaves the tributaries on
 * roughly the lower half of the field by area — the interfluves — and closes over the
 * upper half.
 */
const NET_GATE_LO = 0.70;
const NET_GATE_HI = 1.0;

/**
 * ASPECT — the massif stops being a body of revolution.
 *
 * The radial profile is now evaluated at Reff = R * s, with s = 1 + ASPECT_AMP * a(dir)
 * and `a` a noise field sampled on the UNIT DIRECTION VECTOR (px, pz)/rs, so it is a
 * smooth, seamless, periodic function of azimuth with no atan2 and no branch cut. About
 * five or six aspects around the mountain at ASPECT_SCALE 0.9.
 *
 * WHY THIS AND NOT MORE RELIEF. Azimuthal deviation measured 31.6 m RMS against 537 m of
 * radial relief, and ablation put essentially all 31.6 of it in the main drainage field.
 * A zero-mean relief field added to a body of revolution is still a body of revolution to
 * within its own RMS, so the only way past ~10% is for the PROFILE to depend on direction.
 * At 0.26 the mountain reaches the valley floor 500 m sooner on one aspect than on the
 * opposite one, and the ridge between two such aspects is a real landform rather than a
 * noise feature.
 *
 * WHY IT CANNOT OPEN A BASIN, which is the thing that kills every other way of doing this:
 * s is a function of DIRECTION ONLY, so dh/dR = P'(Reff) * s and s > 0, i.e. the radial
 * derivative has exactly the sign the profile's does, everywhere, at every amplitude.
 * That is a stronger guarantee than the drainage map's rho budget, which only bounds the
 * leak. It is the reason this term is allowed to be much bigger than any relief term.
 *
 * What it does cost is cross-slope gradient: dh/d(arc) = P'(Reff) * ds/dtheta, which at
 * 0.26 and five aspects peaks near 0.08 against the profile's 0.175. That is the constant
 * to trade if the belt gets too steep.
 *
 * Every radial ramp downstream (the drainage amplitude, the valley fade, the bench growth)
 * is fed Reff rather than R, so the relief fades where the profile actually flattens
 * rather than at a fixed radius. Getting that wrong leaves a ring of flat relief sitting
 * on a still-descending flank.
 */
const ASPECT_SCALE = 0.9;
const ASPECT_AMP = 0.40;
/**
 * ASPECT, SECOND OCTAVE — the R8 term, and the one that answers "87% of the azimuthal
 * relief is k=2/3 lobing of the same cone; the k=5-12 band where subsidiary ridges and
 * spurs live did not move".
 *
 * R7 raised the azimuthal RMS from 32 m to 65 m and the critic was right that it bought
 * nothing: measured with a harmonic decomposition of the contour rings (the instrument
 * R7's single RMS number could not have had), 82% of that 65 m sits in k <= 4 and the
 * k=5-12 band read 23.8 m — against 21 m in R6. One octave of aspect at ASPECT_SCALE 0.9
 * traces a circle of radius 0.9 through the noise lattice, which is FOUR features per
 * revolution and cannot be anything else. A four-lobed cone is still a cone.
 *
 * A spur is a k=6-12 feature: eight or ten ridges running off a summit, each with its own
 * valley beside it. So the aspect field gets a second octave at 1.9, which traces a circle
 * of radius 1.9 and crosses about nine features per revolution.
 *
 * It is worth being precise about why this is the right term rather than more relief. The
 * aspect distortion evaluates the profile at Reff = R * s(direction), so the ridge it
 * makes is part of the mountain's BODY — the ground on that bearing is genuinely higher
 * all the way from the summit to the runout and reaches the valley floor further out.
 * A relief field added on top is zero-mean by construction and can only ever be a texture
 * ON a cone. That is the whole content of the critic's "still a body of revolution".
 *
 * And it is free of basin risk at any amplitude, for the reason the first octave is: s
 * depends on DIRECTION only, so dh/dR = P'(Reff) * s has exactly the sign P' has,
 * everywhere. The cost is cross-slope gradient, P'(Reff) * ds/d(arc) * R, which scales as
 * AMP * SCALE — the second octave at 0.15/1.9 costs about the same as the first at
 * 0.46/0.9, which is why the first came down from 0.60 to pay for it. Measured, the two
 * together hold the total azimuthal RMS while moving 26 m of it out of k<=4 and into
 * k=5-12.
 */
const ASPECT2_SCALE = 3.0;
const ASPECT2_AMP = 0.42;

/**
 * THE TILT — an EXACT first harmonic in the aspect scale, and the band the critic named.
 *
 * R8 measured k=1 at 5.5 m out of 63.1 m of azimuthal relief and it had not moved in two
 * rounds. It could not: both aspect octaves are noise sampled on a circle through the
 * lattice, and a circle of radius 0.9 or 3.0 crosses four or nine features — whatever it
 * crosses, k=1 is whatever the noise happens to leave there, which is nothing. There is no
 * amplitude anywhere in this file that k=1 is a function of.
 *
 * So it is authored rather than sampled. `d = (dir . axis)` for a fixed unit axis is
 * cos(theta - theta0) EXACTLY — one harmonic, no leakage into any other k, and it is
 * measurable as such on a ring. The ground is higher where the dot product is positive
 * (note the minus in `sAsp`): the massif has a HIGH SIDE, on the TILT_C bearing, whose
 * ground stands ~2*TILT_AMP*FALL_GRADIENT*R above the opposite bearing at the same radius
 * and whose foot reaches (1+TILT_AMP)/(1-TILT_AMP) further out.
 *
 * WHY IT IS ALLOWED TO BE THE LARGEST AZIMUTHAL TERM IN THE FILE. Cross-slope gradient is
 * P'(Reff) * ds/d(arc) = P' * ds/dtheta, and ds/dtheta for a k harmonic goes as k times its
 * amplitude. k=1 is therefore the CHEAPEST metre of azimuthal relief in the file by a
 * factor of nine against ASPECT2 — 0.175 * TILT_AMP of cross-slope for the whole thing.
 * And it inherits the aspect guarantee unchanged: s is a function of DIRECTION ONLY, so
 * dh/dR = P'(Reff) * s keeps P's sign everywhere and no basin can open at any amplitude.
 *
 * WHAT IT IS NOT. Measured (captures/w5-terrain-r9-tools/sweep.mjs), a k=1 term alone is
 * close to tilting the whole world by 0.175*TILT_AMP: to first order P(R(1+t*cos)) =
 * P(R) - FALL*t*(p . axis). It gives the massif a front and a back and it moves the
 * skyline, but a plane has no shoulder in it, which is why THE CAP below is the other
 * half of this round and why raising this alone would have been R7's mistake again.
 */
const TILT_AMP = 0.26;
/** Bearing of the high side, as a unit vector in world (x, z). Chosen by sweep, not by
 *  taste: it is the orientation that leaves findSpawn's trailhead under the vegetation
 *  piece's 690 m treeline on a rideable opening pitch while putting the massif's high
 *  shoulder into the spawn's forward frame. See the R9 header. */
const TILT_CX = -0.883;
const TILT_CZ = -0.469;

/**
 * THE CAP — the massif gets a top.
 *
 * THE MEASUREMENT THIS ROUND TURNS ON, because it reversed the plan. The gap says the
 * skyline is horizontal, and four rounds have answered gaps like that with more azimuthal
 * relief. Swept on the shipped function with a horizon ray-march from the spawn
 * (captures/w5-terrain-r9-tools/skyline.mjs, validated against three analytic worlds where
 * the answer is known in closed form), TRIPLING both aspect octaves — azimuthal RMS 63 m
 * -> 147 m, more than twice what any critic has asked for — moves the worst 65-degree
 * window of the skyline from 1.33 to 0.60 degrees. It gets WORSE. That is not a tuning
 * result, it is structural: `s` depends on direction only, so along any outward ray the
 * ground descends monotonically at a rate that is constant on that ray, and the skyline in
 * the downhill half is the far plane on every bearing no matter how big `s` gets.
 *
 * What the skyline is made of, then, is RADIAL structure — and the radial profile has
 * exactly one feature in it, a constant gradient, with its FLATTEST part at the apex
 * (APEX_FRAC rounds the top over 168 m). The summit is the flattest ground on the mountain.
 * A mountain whose top is its flattest part has no silhouette, and no amount of azimuthal
 * anything will give it one.
 *
 * So the profile's gradient is multiplied up inside CAP_R into a summit block:
 *
 *     hcap(R, dir) = A(dir) * (1 - t)^CAP_N * (1 + CAP_N*t),   t = min(1, R / CAP_R)
 *
 * with dhcap/dt = -A*CAP_N*(CAP_N+1)*t*(1-t)^(CAP_N-1). Read off that expression:
 *   dhcap/dt <= 0 for A > 0            -- it can only ever STEEPEN, so dh/dR keeps the
 *                                         sign the profile gives it and NO BASIN can open,
 *                                         at any amplitude. Same class of guarantee as
 *                                         ASPECT and TWIST, not a budget to be watched.
 *   dhcap/dt = 0 at t = 1              -- C1 onto the untouched profile at CAP_R.
 *   dhcap/dt = 0 at t = 0              -- the tip is rounded, not a cone point, so the
 *                                         normal is continuous over the summit.
 *
 * CAP_R IS A WORLD RADIUS, not a Reff. That is deliberate and it is the safety property
 * that matters to the ride gate: the trail network's highest node sits at R = 1740 m
 * (measured off the fullrun telemetry, min radius 1740, max 3206), so a cap that is zero
 * outside R = 1500 cannot move one metre of built trail, whatever `s` does. In Reff it
 * would reach world R = CAP_R / s_min and land on the top of Sunday Best.
 *
 * A(dir) carries the asymmetry — CAP_TILT leans the summit block onto the same high side
 * as THE TILT, CAP_LOBE gives it the aspect field's ~9 lobes so it is a peak with ridges
 * running off it rather than a cone. A(dir) > 0 is required for the monotonicity above and
 * is checked at build time, not assumed: see the CAP_POSITIVE invariant.
 *
 * WHAT IT BUYS, measured: the summit stands 250 m higher, which from the trailhead is
 * 15.4 degrees of elevation against the 10.2 degrees of intervening ground — i.e. it
 * becomes the skyline instead of hiding behind the next rise — and it is 400 m of snow
 * above SNOW_LINE rather than 200.
 */
const CAP_R = 1400;
const CAP_AMP = 400;
/** Shape exponent. Higher concentrates the steepening near the tip: max |dhcap/dR| is
 *  A*(CAP_N+1)*(1-1/CAP_N)^(CAP_N-1) / CAP_R, at t = 1/CAP_N. At 3 that is 1.78*A/CAP_R,
 *  which with the constants above is 0.30 of extra gradient on top of the profile's
 *  0.175 — a 47% summit face, steep enough to read as a peak and nowhere near the 100%
 *  where the clipmap's coarse rings would alias the normal. */
const CAP_N = 3;
const CAP_TILT = 0.35;
const CAP_LOBE = 0.30;
/** Bound on |noise| for this generator: 2D gradient noise with unit gradients cannot
 *  exceed sqrt(1/2). Used in the CAP's amplitude bound and in PEAK. */
const NOISE_MAX = 0.7072;

/**
 * THE OUTLIERS — the ground the rider looks DOWN at, and the half of the gap the cap
 * cannot reach.
 *
 * MEASURED FIRST, because this term exists to answer a number. Ray-marching the horizon
 * from the shipped spawn (skyline.mjs), the downhill half of the panorama has its skyline
 * formed at the FAR PLANE on every bearing, at a constant -6.5 degrees, varying by 1.3
 * degrees over a whole 65-degree frame. That is the horizontal line in farhill_4p0s and
 * farhill_13p0s, and here is why it is horizontal and why nothing tried so far could move
 * it: the drainage network SUBTRACTS. Channels cut down from a datum, so the highest
 * ground on any far ring is the un-incised datum — which is the axisymmetric profile,
 * exactly. The silhouette of an eroded cone is the cone.
 *
 * So the far field gets something ADDITIVE and positive: outlying hills beyond the foot of
 * the massif, at a 2.6 km wavelength and up to FOOT_AMP/2 tall, so the valley the rider is
 * descending into has a far side. `v*v` rather than `v` keeps it non-negative — it can
 * raise ground into a hill but never dig a pit below the runout floor — and it is smooth,
 * unlike max(0, v), so the normal is continuous through every trough.
 *
 * IT CANNOT REACH THE RIDE, and that is enforced by two ramps rather than by one:
 *   - a WORLD-radius ramp from FOOT_R0. The trail network's outermost node is at R = 3206
 *     (measured off fullrun telemetry), and smoothstep is zero in both value AND
 *     derivative at its lower edge, so the `if` gate below is exact, not approximate.
 *   - an Reff ramp from FOOT_RE0. Without it, a bearing whose aspect scale is small has
 *     its profile still falling at full gradient out past R = 5 km, and hills dropped on
 *     that flank would be a bulge on a live slope rather than a foothill past the foot.
 * Both must be open for anything to happen, so the hills only appear where the mountain
 * has actually finished.
 *
 * IT CANNOT RAISE PEAK. The Reff ramp opens at 3600 m, where the profile has already spent
 * 615 m of its 818 m; FOOT_AMP/2 is 250. Nothing outside the belt gets within 200 m of the
 * summit block, so reliefHeight does not have to carry this term.
 */
const FOOT_AMP = 500;
const FOOT_SCALE = 2600;
const FOOT_R0 = 3400;
const FOOT_R1 = 4700;
const FOOT_RE0 = 3600;
const FOOT_RE1 = 4900;

/**
 * THE TWIST — R8's answer to "the channels still run summit-to-runout in parallel without
 * a single confluence", and the term this round is really about.
 *
 * WHAT R7 SHIPPED, measured with a tracer that follows each channel floor outward rather
 * than counting minima on a ring (see the CONFLUENCE invariant): 34 channels seeded at
 * R = 1.2 km, 10 confluences, mean lateral drift 176 m = 0.80 of a channel spacing over
 * 1.7 km of descent. That is a five-degree tilt, i.e. straight. The channel COUNT fell
 * from 32 to 27 across the belt only because the trunk spacing went 219 m to 513 m —
 * every channel got 2.3x fatter, which is what the critic saw and why they called it a
 * comb. selftest.mjs shows the count metric cannot tell that apart from convergence.
 *
 * WHAT WAS TRIED FIRST AND IS NOT ENOUGH: displacing the network's own trace along the
 * tangent by a direction-only field times a radial ramp. It works — an ablated,
 * network-only sector map shows the channels visibly curving and bunching — and in the
 * FULL field it is invisible, because the main drainage ribs, the side gullies and the
 * aspect spurs are all still dead radial and they are what the eye reads. Bending one
 * term out of five leaves a comb.
 *
 * SO THE WHOLE DOMAIN TWISTS. Before anything is evaluated, the query point is rotated
 * about the summit by an angle that grows with radius and varies with azimuth:
 *
 *     h(R, theta)  =  H(R, theta + phi(R, theta)),   phi = 2*atan(TWIST * ramp(R) * w(dir))
 *
 * Every feature in the function bends at once — ribs, gullies, benches, trunk channels,
 * tributaries and the aspect spurs — and they bend together, so the mountain stays
 * self-consistent rather than having one term cutting across the others.
 *
 * AND IT IS FREE, which is the part worth being precise about, because every previous
 * round paid for bending in basin risk. For each fixed R the map theta -> theta + phi is
 * a diffeomorphism of the circle onto itself, so h(R, .) and H(R, .) have the same
 * critical points in bijection: every descending path of H maps to a descending path of h
 * with an IDENTICAL height profile, and no pit can be created or destroyed at any
 * amplitude. The aspect term is safe because s depends only on direction; this is safe
 * because it is a change of azimuthal coordinate. Both are structural, and neither is a
 * budget that has to be watched — unlike every relief term in this file.
 *
 * The one condition is that the map really is a bijection: 1 + dphi/dtheta > 0. Past that
 * the ring folds, the fold is a new pair of critical points, and the guarantee is gone. It
 * is measured at build time rather than assumed — see the TWIST_MONOTONE invariant.
 *
 * NO TRIG, exactly. The rotation is built from the Weierstrass substitution:
 * c = (1-u^2)/(1+u^2), s = 2u/(1+u^2) is an exact cosine/sine pair for phi = 2*atan(u),
 * so a noise-driven u gives an exact rotation with one divide and no transcendental, and
 * the angle is bounded by construction.
 *
 * WHAT IT COSTS: the azimuthal compression 1 + dphi/dtheta multiplies every cross-slope
 * gradient, so where channels bunch the ground is locally steeper by that factor. That,
 * and only that, is what bounds TWIST.
 */
const TWIST = 0.16;
/** Lobes of the twist field around the ring: a noise circle of radius 1.6 crosses about
 *  six or seven features, so the twist varies over five or six channel spacings and
 *  neighbouring channels turn by different amounts. Higher bunches harder per channel and
 *  buys less total bend for the same monotonicity margin — dphi/dtheta goes as
 *  TWIST * SCALE while phi itself goes as TWIST alone. */
const TWIST_SCALE = 0.8;
/** The twist ramp: nothing at the apex, where the summit dome is a body of revolution and
 *  should be, and full out on the runout, so the bend accumulates downhill the way a
 *  drainage network's does. */
const TWIST_R0 = 400;
const TWIST_R1 = 3400;

/**
 * Terracing. h' = lerp(q, quantise(q), W), which multiplies the accumulated gradient
 * by (1 + W*(dS - 1)) with dS in [0, 1.875] — strictly positive for W < 1. So this
 * flattens benches and steepens the rolls between them and *cannot* invert a slope or
 * open a basin, however hard it is driven. That is why it, and not the noise, is where
 * the 8-25% structure comes from.
 */
/**
 * ... and it is also the *only* source of shape along the fall line. Everything else
 * in this function is 8:1 elongated downhill by construction, which is what stops the
 * relief opening basins but also means a gully floor would otherwise be a ramp with
 * nothing on it for two kilometres. Because the terrace quantises *elevation*, its
 * treads and risers run along the contour — square across the direction of travel —
 * so it is exactly the term that puts something in front of the rider. R3 drove it
 * from 26 m / 0.46 to 26 m / 0.58: at 0.58 the tread gradient is 0.42x nominal and
 * the riser 1.51x, so a 20% pitch becomes an 8% bench rolling into a 30% roll every
 * ~120 m. Measured, the deviation of the 200 m fall-line profile from a straight line
 * went from 5.9 m to 14 m.
 */
/**
 * R4 moved the period 26 -> 48 at unchanged weight, and this is the second half of the
 * launch fix. The terrace adds a curvature of W * |S''| * g^2 / P to the fall line,
 * where g is the local grade — quadratic in grade, because a bench quantises ELEVATION
 * and so its horizontal wavelength collapses as the ground steepens. At the mid-mountain
 * grade of 19% that term is 0.005 /m and nobody notices; on the 80% ground in the p99 of
 * the belt it is 0.08 /m, which throws a rolling wheel at 40 km/h. It is the largest
 * remaining launcher once the ridge crests are rounded: measured, the fraction of the
 * fall line that launches below 40 km/h is 0.28% with rounding alone, 0.02% with rounding
 * and the terrace switched off entirely, and 0.05% with rounding and this period.
 *
 * Period rather than weight, deliberately. Curvature goes as W/P but the bench's own
 * depth goes as W*P, so trading period against curvature makes the benches DEEPER
 * (1.6 m -> 2.9 m) while cutting the weight makes them shallower. Measured on the
 * band-passed height profile — the 20-150 m band, which is what a rider reads two to
 * fifteen seconds ahead — R3 carries 1.81 m RMS, this carries 2.15 m, and the
 * cut-the-weight alternative that scores the same on launches carries 1.37 m. That last
 * one is the version of this fix that would have flattened the mountain, and the only
 * reason it was not shipped is that the metric was built before the constant was chosen.
 *
 * It also halves the number of iso-elevation bands on the face, which is a straight win
 * against the standing "the far field reads as iso-elevation banding" critique, since
 * the terrace is the only term in this function whose features run along the contour.
 */
/**
 * R5 brings this back down, and it is bought rather than argued for.
 *
 * R4's own note derives the constraint exactly: the terrace adds W * |S''| * g^2 / P of
 * convex curvature to the fall line, so period and convex curvature trade one for one.
 * What it did not question is `S`. The quintic smoothstep is SYMMETRIC — its convex half
 * and its concave half have the same peak second derivative — and a bench does not need
 * to be. Only the convex half, the nose of the roll where the tread tips into the riser,
 * can throw a wheel; the concave half is the bottom of the roll, where the fork is
 * already doing the work it exists for.
 *
 * So `S` is now the asymmetric quintic-equivalent with S'(f) = 12 f (1-f)^2 (see
 * TERRACE_MAX_OFFSET). Against the symmetric quintic, at equal W and P:
 *
 *   peak convex |S''|      5.77 -> 4.00     (-31%, and this is the launch term)
 *   peak concave  S''      5.77 -> 12.0     (paid here, where it costs travel not air)
 *   bench depth max|S-f|   0.147 -> 0.2235  (+52%)
 *   peak riser steepening  1.875 -> 1.778   (-5%, gentler risers as a side effect)
 *
 * Two of those compound: the period can come down by the curvature ratio and the bench
 * still gets deeper. 48 -> 33 is that trade taken in full — benches 1.45x more often,
 * 3.7 m deep instead of 3.5 m, at the same worst-case convex curvature R4 shipped.
 *
 * At the 19% mid-mountain grade that is a bench every 174 horizontal metres rather than
 * every 253. It does not get all the way back to R3's 137 m and it is not meant to: the
 * rill field is what carries the 10-50 m band now, and the terrace's job is the 150 m
 * one it was always best at.
 */
const TERRACE_PERIOD = 33;
/**
 * 0.58 -> 0.50, and this one was bought with a regression rather than reasoned to.
 *
 * The tread of a bench runs at (1 - W) times the nominal grade, so at W = 0.58 an 18%
 * hillside has 7.6% treads. That was survivable at P = 26 because a tread was ~50 m long.
 * Doubling the period doubled the tread length too, and the first `fullrun` after the
 * change came back with stalls up 6 -> 8 and the autopilot's average speed down from
 * 14.4 to 12.4 km/h: the trail inherits the terrace, and a bike that has braked for a
 * corner cannot get going again on 100 m of 7.6%.
 *
 * Measured on the raw fall line, longest continuous stretch under 7% grade (p90 of all
 * such stretches) and the fraction of the line under 7%:
 *
 *                        flat<7%   p90 stretch   launch<40   launch<60   shape 20-150 m
 *   R3  P26 W0.58           5%         35 m        0.89%       2.28%         1.81 m
 *   P48 W0.58               8%         57 m        0.05%       0.57%         2.15 m
 *   P48 W0.50               5%         46 m        0.03%       0.49%         1.93 m
 *   P48 W0.44               3%         49 m        0.03%       0.50%         1.79 m
 *
 * W0.50 is the row that gives back the flat ground without giving back the launch fix or
 * the shape — it is better than W0.58 on both launch columns, because a shallower terrace
 * also carries less of the grade-squared curvature the period was raised to control.
 * W0.44 keeps going on flatness and drops the readable shape below R3, which is the point
 * at which this would be flattening the mountain rather than fixing it.
 */
const TERRACE_WEIGHT = 0.50;
/**
 * max |S(f) - f| for the asymmetric quantiser, which is what sets bench depth and what
 * PEAK has to bound. S(f) = 3f^4 - 8f^3 + 6f^2, so S - f is stationary where
 * 12f^3 - 24f^2 + 12f - 1 = 0; the root that matters is f = 0.6431 and the value there
 * is 0.22354. It is emphatically not at f = 0.5, and neither was the quintic's.
 */
const TERRACE_MAX_OFFSET = 0.22354;
/** Vertical wander of the bench phase, m. Roughly half a period so benches on one
 *  spur do not line up with the benches on the next one — so it tracks TERRACE_PERIOD. */
const TERRACE_JITTER = 17;
/** Ground wavelength of that wander, m. Long enough that a single bench still reads as
 *  a bench for a hundred metres or so, short enough that it does not run round the
 *  whole mountain at one elevation. */
const TERRACE_PHASE_SCALE = 260;

/** Sub-metre surface undulation. Kept low-amplitude and above ~6 m wavelength so the
 *  coarse clipmap rings do not alias it into shimmering normals. */
const CHATTER_SCALE = 46;
const CHATTER_AMP = 0.85;

/**
 * Highest ground on the mountain, m. Exported as reliefHeight; consumers normalise
 * altitude against it, so it has to be at or above the true maximum or their normalised
 * altitude runs past 1 on the summit block.
 *
 * R4 broke this and it is worth recording how, because the bound looked airtight. The
 * profile-plus-crest term alone is only a bound on the drainage field: it silently
 * assumed the terrace and the chatter could not push the summit any higher. They can, and
 * R3 got away with it only because rounding-free ridged noise almost never reaches its
 * own crest value, so there were 11 m of accidental slack. Rounding the crests took that
 * slack away and the true maximum came out 2.4 m ABOVE reliefHeight. The two missing
 * terms are now added explicitly rather than left to luck:
 *
 *   chatter, which is bounded by its amplitude;
 *   the terrace, bounded by W * P * max|S(f) - f| and max|S(f)-f| = 0.147 for the quintic
 *     (at f = 0.243 and 0.757 — it is NOT 0.5, where S(f) = f exactly).
 *
 * Measured after the change: true max ground 1058.7 m by a refined 2.5 m grid search over
 * the summit block, reliefHeight 1061.2 m. Margin 2.6 m, and it is now a real bound
 * rather than an observation. Re-measure it if you touch the terrace or the chatter.
 *
 * R5 adds a fourth term and re-measures: true max ground 1060.0 m by the same search
 * (10 m coarse then 0.5 m refined over a 1.8 km box on the summit), reliefHeight 1064.1 m,
 * margin 4.1 m. The rill's term is written against the apex rather than against
 * RILL_AMP outright because RILL_AMP is the amplitude at DRAIN_REF and the field is
 * scaled by 1/kr — at the apex kr is 4.56, so the honest bound is a seventh of the naive
 * one, and the naive one was costing 15 m of slack that everything normalising against
 * reliefHeight would have paid for.
 */
/**
 * R6 re-measured it after the amplitude change, since the rill's term in the bound grew
 * from 2.05 m to 5.14 m: true max ground 1062.77 m by the same search (10 m coarse then
 * 0.5 m refined over a 2.4 km box on the summit), reliefHeight 1065.97 m, margin 3.20 m.
 */
/**
 * R7 re-derives it against the annulus trace. The network is still SUBTRACTED, so the only
 * way either generation raises ground is on an interfluve where it sits below its own
 * datum, bounded by (datum - field minimum) times the amplitude — but the amplitude is now
 * NET_AMP / gN rather than RILL_AMP / kr, and gN at the apex is (ref/core)^(1+NET_M) rather
 * than (ref/core)^(1-RILL_RHO), which is a different number in the safe direction.
 *
 * The profile term is unchanged by ASPECT: Reff = R * s and the profile's maximum is at
 * Reff = 0, which is R = 0 for any positive s, so SUMMIT is still the exact bound.
 *
 * Measured after the change: true max ground 1051.09 m (10 m coarse then 0.5 m refined over
 * a 2.4 km box on the summit), reliefHeight 1055.72 m, margin 4.63 m.
 */
/** gN at the apex, (DRAIN_REF / DRAIN_CORE)^(1+NET_M), where the true maximum is. */
const GN_APEX = Math.pow(DRAIN_REF / DRAIN_CORE, 1 + NET_M);
/**
 * Minimum of the one-octave rounded ridged field. R6 measured 0.1473 over 4e5 belt
 * samples with the terrace off; this is left at the older, smaller 0.132 deliberately,
 * because it enters the bound as (NET_MID - NET_FIELD_MIN) and a smaller value makes
 * reliefHeight larger, which is the safe direction. It is a bound, not an observation.
 */
const NET_FIELD_MIN = 0.132;
/**
 * R9 adds THE CAP, which is the first term in five rounds to raise the profile's own
 * maximum rather than sit on it. hcap(R) = A(dir) * (1-t)^3 * (1+3t) is maximal at t = 0
 * with value A(dir), and A(dir) = CAP_AMP * (1 + CAP_TILT*d + CAP_LOBE*a2) with |d| <= 1
 * and |a2| <= NOISE_MAX, so CAP_AMP * (1 + CAP_TILT + CAP_LOBE*NOISE_MAX) bounds it — a
 * bound, not an observation, and loose by design because loose is the safe direction for
 * anything normalising against reliefHeight.
 */
const PEAK =
  SUMMIT +
  CAP_AMP * (1 + CAP_TILT + CAP_LOBE * NOISE_MAX) +
  DRAIN_AMP_HIGH * (1 - DRAIN_MID) +
  CHATTER_AMP +
  TERRACE_WEIGHT * TERRACE_PERIOD * TERRACE_MAX_OFFSET +
  // Trunk, then tributary at its own amplitude and its steepest wavenumber ratio (the gate
  // is <= 1 so it is dropped from the bound). The apex is where the maximum is and where
  // both amplitudes are smallest: moving out to R = 600 m would buy the field a couple of
  // metres more headroom and cost the profile 38 m, so the maximum cannot migrate outward.
  (((NET_GRAD_HI + NET_TRIB_GRAD_HI / NET_NT_HI) * NET_SCALE) * (NET_MID - NET_FIELD_MIN)) / GN_APEX;

/**
 * Material bands, absolute metres. These are shared with the vegetation piece by
 * value, not by import: it puts its treeline at 690 m, so the alpine line has to sit
 * *above* that or the ground says bare scree while firs are growing out of it. R2 had
 * ALPINE_LINE at 600, which put the trailhead (675 m) into scree with a forest on it —
 * the whole opening view read as grey moraine. 725 m puts the trailhead on hardpack,
 * leaves a genuine alpine band from just above treeline to the snow, and caps the
 * summit block at 850 m out of a 993 m peak.
 */
const SNOW_LINE = 850;
const ALPINE_LINE = 725;
const FOREST_LINE = 460;

// --------------------------------------------------------------------------------
// Self-audit. Three landform invariants, measured once on the raw mountain at boot.
//
// They exist because this piece shipped the same defect four rounds running and each
// round argued from a number that was not on the report. A critic reading SUMMARY.md now
// sees the convergence law and the axisymmetry directly, and cannot be told "the massif
// has more shape this round" without it.
// --------------------------------------------------------------------------------

/**
 * The convergence law. A drainage network merges downhill, so a contour ring low on the
 * mountain must cross FEWER channels than one high on it.
 *
 * Definition, and it is the critic's, reproduced: a channel is a local minimum of the
 * height around a contour ring whose circular PROMINENCE is at least 4 m. Measured on the
 * shipped R6 function that gives 37 channels at R = 700 m and 74 at R = 2600 — the 37 is
 * the critic's own number to the unit, which is how this metric was validated.
 *
 * Reported as far/near, so 1.0 is "the network multiplies rather than converges" and
 * anything under 1 converges. Banded over five rings each side: a single ring's count
 * moves +-25% under a change that moves nothing else, because several minima sit within a
 * metre of the threshold, and a ratio of two single rings is therefore not a measurement.
 */
const CHANNEL_CONVERGENCE = declareInvariant({
  id: 'terrain.channelConvergence',
  owner: 'terrain',
  what: 'channels crossing a contour ring low on the mountain, over channels crossing one high on it',
  unit: 'ratio',
  budget: 1.0,
  compare: 'max',
  severity: 'error',
  phase: 'build',
  source: 'src/world/terrain.ts NET_M (the annulus trace exponent) and NET_TRIB_GRAD_HI',
  consequence: 'channels that multiply downhill instead of merging read as combed radial spokes,'
    + ' and a rider never arrives anywhere — every gully looks like the last one',
});

/**
 * Axisymmetry. How far the mountain departs from a body of revolution, as a fraction of
 * the relief it carries radially.
 *
 * RMS over azimuth of (height - the azimuthal mean at that radius), averaged over the
 * rings from 1.4 to 2.6 km, divided by the azimuthal-mean drop from R = 500 to R = 3400.
 * R6 measures 31.6 m against 537 m — 5.9%, i.e. the massif is 94% cone.
 */
const AXISYMMETRY = declareInvariant({
  id: 'terrain.azimuthalRelief',
  owner: 'terrain',
  what: 'azimuthal departure from a body of revolution as a fraction of the radial relief',
  unit: 'frac',
  budget: 0.12,
  compare: 'min',
  severity: 'error',
  phase: 'build',
  source: 'src/world/terrain.ts ASPECT_AMP',
  consequence: 'a cone reads as one hillside from every direction: nothing on the skyline tells a'
    + ' rider where they are, and every aspect of the mountain is the same ride',
});

/**
 * CONFLUENCE — the invariant R7 needed and did not have, and the reason this piece was
 * sent back a fifth time.
 *
 * CHANNEL_CONVERGENCE above counts minima on a ring, and a falling count is NOT evidence
 * that anything joined: a field whose channels stay exactly parallel while alternate ones
 * fade and the survivors widen has a count that halves and no confluences at all. That is
 * not a hypothetical, it is what R7 shipped and what the critic saw on a detrended sector
 * map, and `captures/w5-terrain-r8-tools/selftest.mjs` demonstrates it on a synthetic
 * field where the answer is known: PARALLEL, FATTEN and CONFLUENT give counts of
 * 24->24, 24->12 and 24->12, so the count metric scores FATTEN and CONFLUENT identically.
 * The tracer below separates them 0 / 0 / 12.
 *
 * This one seeds a tracer in every channel floor at R = 1.2 km and walks each outward,
 * sliding tangentially to stay in the local minimum. Two tracers whose dividing ridge
 * collapses while BOTH still have a divide of their own on the far side have physically
 * joined; one whose divide collapses because it simply faded out has not, and is counted
 * as a death instead. Reported as the fraction of seeded channels that join another.
 */
const CONFLUENCE = declareInvariant({
  id: 'terrain.channelConfluence',
  owner: 'terrain',
  what: 'fraction of channels seeded high on the mountain that merge into another one going downhill',
  unit: 'frac',
  budget: 0.25,
  compare: 'min',
  severity: 'error',
  phase: 'build',
  source: 'src/world/terrain.ts TWIST and NET_MELT',
  consequence: 'a comb of parallel gullies that never join is not a drainage network: every'
    + ' gully looks like the last one and a rider never arrives anywhere',
});

/**
 * THE SPUR BAND. Azimuthal relief in wavenumbers 5-12 as a share of all of it.
 *
 * AXISYMMETRY above is a single RMS, and R7 doubled it (32 m -> 65 m) while the critic
 * still called the massif a body of revolution — correctly, because 80% of that 65 m is
 * k = 2-3, which is a cone with a couple of lobes on it. A spur, a subsidiary ridge and
 * the valley between them are k = 6-12 features. Splitting the same ring by harmonic is
 * what makes the difference visible, and it is the number that stops "the azimuthal RMS
 * went up" from being an answer.
 */
const SPUR_BAND = declareInvariant({
  id: 'terrain.spurBandShare',
  owner: 'terrain',
  what: 'share of azimuthal relief carried by wavenumbers 5-12, where spurs and subsidiary ridges live',
  unit: 'frac',
  budget: 0.22,
  compare: 'min',
  severity: 'error',
  phase: 'build',
  source: 'src/world/terrain.ts ASPECT2_AMP and ASPECT2_SCALE',
  consequence: 'all the azimuthal shape in two or three lobes is a cone seen from the side:'
    + ' no ridgeline, no spur and nothing on a 2 km skyline',
});

/**
 * THE TWIST'S OWN PRECONDITION, measured rather than assumed.
 *
 * The twist is basin-free because theta -> theta + phi is a bijection of each contour
 * ring, which holds exactly while 1 + dphi/dtheta > 0. Past zero the ring folds, a fold is
 * a new pair of critical points, and the guarantee that carries the whole term is gone —
 * silently, because the height field is still smooth and still looks fine. So it is
 * checked at build time, on the shipped field, at the shipped constants.
 */
const TWIST_MONOTONE = declareInvariant({
  id: 'terrain.twistMonotone',
  owner: 'terrain',
  what: 'smallest azimuthal stretch of the twist map; at or below zero the contour ring folds',
  unit: 'frac',
  budget: 0.15,
  compare: 'min',
  severity: 'fatal',
  phase: 'build',
  source: 'src/world/terrain.ts TWIST x TWIST_SCALE',
  consequence: 'a folded ring is a closed bowl the twist was proved incapable of making —'
    + ' the proof, not the terrain, is what breaks first',
});

/**
 * THE FLAT SKYLINE — R9's invariant, and the only one on this list that measures the
 * PICTURE rather than the field.
 *
 * Every other landform invariant here is a statistic of a contour ring. Four rounds have
 * moved a ring statistic without moving a screenshot, and R8's was the clearest case yet:
 * it raised the k=5-12 spur band by 67% and the critic's reply was still "every capture
 * reads as broad rolling open ground". A ring statistic cannot be told apart from the
 * picture by argument, so this one is the picture.
 *
 * For each of a set of (eye, bearing) pairs on the rideable belt, march the horizon the
 * way the rasteriser does — for every azimuth in a 65-degree frame, the largest elevation
 * angle any ground along that azimuth subtends inside the 3.2 km far plane — and take the
 * range of that skyline across the frame. Reported: the FRACTION OF FRAMES whose skyline
 * spans less than SKYLINE_FLAT_DEG. That is, in one number, "how often does a capture come
 * back with a horizontal line across it".
 *
 * VALIDATED BY FALSIFICATION, not by looking right: `skyline.mjs --selftest` runs the same
 * march over three analytic worlds where the answer is known in closed form — a plain cone
 * (the defect), the same cone with a summit block, and the same cone tilted — and it has
 * to separate them. It does: worst-window 0.00 / 0.00 / 0.26 deg and full-circle range
 * 15.5 / 24.1 / 13.0. The cone and the mountain differ only where the instrument is
 * pointed at the summit, which is exactly the discrimination this invariant needs and
 * exactly what the ring statistics do not have.
 */
const SKYLINE_FLAT = declareInvariant({
  id: 'terrain.flatSkylineFrames',
  owner: 'terrain',
  what: 'share of framings from the rideable belt whose whole skyline fits inside two degrees',
  unit: 'frac',
  budget: 0.3,
  compare: 'max',
  severity: 'error',
  phase: 'build',
  source: 'src/world/terrain.ts CAP_AMP and CAP_R',
  consequence: 'a horizontal line across the top of the frame: the massif has no top, no shoulder'
    + ' and no silhouette, and a rider cannot see where they are going to end up',
});

/**
 * THE FIRST HARMONIC. Azimuthal relief at k = 1 — the band that gives a mountain a front
 * and a back — in metres RMS on the same rings AXISYMMETRY uses.
 *
 * It is separate from SPUR_BAND and from AXISYMMETRY because it is the band that R7 and R8
 * both left untouched while moving the other two: 6.5 m then 5.5 m out of 63.1 m of total
 * azimuthal relief. Neither round could have moved it, and neither round could see that it
 * had not: k=1 is not a function of any amplitude in this file unless something authors it,
 * because a noise circle puts its energy wherever it crosses features and that is never
 * once per revolution. Budgeting it separately is what stops the next round reporting
 * "azimuthal RMS is up" as an answer to "the mountain has no high side".
 */
const K1_RELIEF = declareInvariant({
  id: 'terrain.azimuthalK1',
  owner: 'terrain',
  what: 'azimuthal relief in the first harmonic, the one that gives the massif a high side and a low side',
  unit: 'm',
  budget: 20,
  compare: 'min',
  severity: 'error',
  phase: 'build',
  source: 'src/world/terrain.ts TILT_AMP',
  consequence: 'with no k=1 the mountain is the same height on every bearing at every radius:'
    + ' no shoulder, no aspect worth choosing, and every side of it is the same ride',
});

/**
 * THE CAP'S OWN PRECONDITION, measured rather than assumed — the same discipline
 * TWIST_MONOTONE applies to the twist.
 *
 * hcap is monotone non-increasing in R, and therefore incapable of opening a basin, only
 * while its direction-dependent amplitude A(dir) is positive. Past zero the cap becomes a
 * PIT centred on the summit: still smooth, still plausible on a screenshot, and the
 * guarantee that lets this term be 250 m tall is gone. So the minimum of A over the whole
 * ring set is measured on the shipped field at the shipped constants.
 */
const CAP_POSITIVE = declareInvariant({
  id: 'terrain.capAmplitude',
  owner: 'terrain',
  what: 'smallest summit-block amplitude over all bearings; at or below zero the block inverts into a pit',
  unit: 'm',
  budget: 20,
  compare: 'min',
  severity: 'fatal',
  phase: 'build',
  source: 'src/world/terrain.ts CAP_TILT + CAP_LOBE against CAP_AMP',
  consequence: 'a negative cap is a crater on the summit — the monotonicity proof that lets the'
    + ' cap be this big fails silently and the fall line dies at the top of the mountain',
});

/**
 * The basin budget. Every relief term in this file leaks some RADIAL gradient, and where
 * that leak beats the profile's fall gradient the fall line goes uphill and a descent
 * dies in a closed bowl — the R1 failure the whole domain-map design exists to prevent.
 *
 * Fraction of the rideable belt (R 900-3200, where the profile is still descending on
 * every aspect) whose gradient points uphill AND outward. R6 measures 0.06%; R2, which
 * introduced the domain map, called 0.5-1.1% acceptable.
 */
const BASIN = declareInvariant({
  id: 'terrain.uphillOutward',
  owner: 'terrain',
  what: 'fraction of the rideable belt whose ground rises as it goes away from the summit',
  unit: 'frac',
  budget: 0.011,
  compare: 'max',
  severity: 'error',
  phase: 'build',
  source: 'src/world/terrain.ts NET_GRAD_HI x NET_M, and ASPECT2_AMP',
  consequence: 'a fall line that goes uphill is a closed bowl: a descent stops dead in it and the'
    + ' trail router has to either climb out or cut a bench across the whole face',
});

/** The skyline audit's lattice. See SKYLINE_FLAT and auditSkyline.
 *  8 eyes x 72 azimuths x ~90 marched steps is about 52k samples, ~14 ms once at boot
 *  against a 35 ms clipmap build. The eye radii walk the belt the trail network actually
 *  occupies (measured off fullrun telemetry: R 1740 to 3206). */
const SKY_EYES = 8;
const SKY_AZ = 72;
const SKY_EYE_R = [1800, 2300, 2050, 2800, 1900, 3100, 2500, 2200];
/** Chase-camera deck height above ground, m. */
const SKY_EYE_UP = 2.2;
/** Tuning.world.viewDistance. Past the far plane the frame is fog, not ground, so a march
 *  that runs further is measuring a skyline the player is never shown. Kept as a literal
 *  because tuning.ts is not this piece's to import from. */
const SKY_FAR = 3200;
const SKY_STEP0 = 8;
const SKY_STEPK = 0.006;
/** A skyline flatter than this across a whole frame is the horizontal line the gap names. */
const SKY_FLAT_DEG = 2.0;

/** Samples per contour ring. 512 is 3.7 m of arc at R = 300 and 34 m at R = 2800 — fine
 *  enough for the finest thing this file puts on a ring at either radius. */
const AUDIT_RING_SAMPLES = 512;
/** Metres of circular prominence a local minimum needs to count as a channel. This is the
 *  critic's threshold, and 4 m reproduces their "37 channels at R = 700" on R6 exactly. */
const AUDIT_CHANNEL_PROMINENCE = 4;
const AUDIT_NEAR_R = [600, 650, 700, 750, 800];
const AUDIT_FAR_R = [2400, 2500, 2600, 2700, 2800];
const AUDIT_DEV_R = [1400, 1800, 2200, 2600];
const AUDIT_RELIEF_R0 = 500;
const AUDIT_RELIEF_R1 = 3400;
const AUDIT_BASIN_RINGS = 24;
const AUDIT_BASIN_SPOKES = 128;
/** Confluence tracer. 1024 samples is 7.4 m of arc at R = 1.2 km, a fifth of the finest
 *  thing the network puts on a ring there; 50 m of radial step keeps the whole walk to
 *  35 rings, i.e. 36k samples and about 12 ms once at boot. */
const AUDIT_TRACE_R0 = 1200;
const AUDIT_TRACE_R1 = 2900;
const AUDIT_TRACE_DR = 50;
const AUDIT_TRACE_M = 1024;
/** Harmonics removed from a ring before a channel is looked for in it. The channels live
 *  at k = 25-40 here and the aspect lobing at k = 1-4, so removing k <= 6 leaves the
 *  channel structure untouched and takes the lobe out. Without it a tracer walks DOWN THE
 *  LOBE rather than down a channel and reports drift that is not there. */
const AUDIT_DETREND_K = 6;
/** Most a channel floor may move sideways per metre of descent and still be the same
 *  channel: tan(60 deg). A confluence bends far less; a tracer that wants more is crossing
 *  a divide. */
const AUDIT_TRACE_BEND = 1.73;
/** Two floors this close in arc are one floor. */
const AUDIT_MERGE_ARC = 10;

/**
 * Local minima around a closed ring whose prominence reaches `minProm`.
 *
 * Prominence, not depth: a channel is a minimum you have to climb `minProm` out of to
 * reach anything lower. Walking each way until something lower than the minimum is found
 * and taking the smaller of the two enclosing maxima is the standard definition, and it
 * is what makes the metric ignore the chatter without needing a low-pass filter that
 * would have its own cutoff to argue about.
 */
function countChannels(h: Float64Array, minProm: number): number {
  const n = h.length;
  let count = 0;
  for (let i = 0; i < n; i++) {
    const b = h[i];
    if (!(b < h[(i - 1 + n) % n] && b <= h[(i + 1) % n])) continue;
    let maxL = b;
    let okL = false;
    for (let k = 1; k < n; k++) {
      const v = h[(i - k + n) % n];
      if (v < b) { okL = true; break; }
      if (v > maxL) maxL = v;
    }
    let maxR = b;
    let okR = false;
    for (let k = 1; k < n; k++) {
      const v = h[(i + k) % n];
      if (v < b) { okR = true; break; }
      if (v > maxR) maxR = v;
    }
    // The ring's global minimum finds nothing lower either way; its prominence is the
    // whole ring's range, so it always counts.
    let prom: number;
    if (okL && okR) prom = (maxL < maxR ? maxL : maxR) - b;
    else if (okL) prom = maxL - b;
    else if (okR) prom = maxR - b;
    else prom = (maxL > maxR ? maxL : maxR) - b;
    if (prom >= minProm) count++;
  }
  return count;
}

const t3: [number, number, number] = [0, 0, 0];
const w3: [number, number, number] = [0, 0, 0];
const v3: [number, number, number] = [0, 0, 0];
const as3: [number, number, number] = [0, 0, 0];
const as3b: [number, number, number] = [0, 0, 0];
const tw3: [number, number, number] = [0, 0, 0];
const nw3: [number, number, number] = [0, 0, 0];
const tr3: [number, number, number] = [0, 0, 0];
const ft3: [number, number, number] = [0, 0, 0];
const prof2: [number, number] = [0, 0];

/**
 * Radial elevation profile. Writes [height, dHeight/dRadius].
 *
 * Piecewise: a parabolic summit dome, a constant-gradient mid-mountain, a quadratic
 * runout, then flat. C1 everywhere, which matters — a kink here would show up as a
 * ring-shaped crease around the whole mountain.
 */
function profile(R: number, out: [number, number]): void {
  const u = R / BASE_RADIUS;
  const a = APEX_FRAC;
  const b = RUNOUT_FRAC;
  let d: number;
  let dd: number;
  if (u < a) {
    d = (u * u) / (2 * a);
    dd = u / a;
  } else if (u < 1 - b) {
    d = a / 2 + (u - a);
    dd = 1;
  } else if (u < 1) {
    const t = (u - (1 - b)) / b;
    d = a / 2 + (1 - b - a) + b * (t - (t * t) / 2);
    dd = 1 - t;
  } else {
    d = a / 2 + (1 - b - a) + b / 2;
    dd = 0;
  }
  out[0] = SUMMIT - FALL_GRADIENT * BASE_RADIUS * d;
  out[1] = -FALL_GRADIENT * dd;
}

/** smoothstep with its derivative packed into `out` as [value, d/dx]. */
function smoothstepD(e0: number, e1: number, x: number, out: [number, number]): void {
  const inv = 1 / (e1 - e0);
  const t = clamp01((x - e0) * inv);
  out[0] = t * t * (3 - 2 * t);
  out[1] = t > 0 && t < 1 ? 6 * t * (1 - t) * inv : 0;
}

const ss2: [number, number] = [0, 0];
const ss2b: [number, number] = [0, 0];
const ss2c: [number, number] = [0, 0];
const ss2d: [number, number] = [0, 0];
const ss2e: [number, number] = [0, 0];
const ss2f: [number, number] = [0, 0];
const ss2g: [number, number] = [0, 0];
const ss2h: [number, number] = [0, 0];

export class Terrain {
  private readonly base: Noise2D;
  private readonly warp: Noise2D;
  private readonly detail: Noise2D;
  private readonly phase: Noise2D;
  private readonly rill: Noise2D;
  /** Aspect field. Sampled on the unit direction vector, so it is a function of azimuth
   *  alone and cannot contribute radial gradient. See ASPECT_AMP. */
  private readonly aspect: Noise2D;
  /** The network's amplitude modulation, sampled on the rotated unit direction. Same
   *  instance R7 used for its world-space azimuthal warp, which the twist replaced. */
  private readonly netw: Noise2D;
  /** The twist field. A function of the INCOMING direction — it is what defines the
   *  rotation, so it cannot be read at the rotated point. See TWIST. */
  private readonly twist: Noise2D;
  private overlay: TrailOverlay | null = null;

  /** Highest ground on the mountain, m. Consumers use this to normalise altitude. */
  readonly reliefHeight = PEAK;
  /** World position of the summit — handy for anything that wants to point at it. */
  readonly summitX = MASSIF_X;
  readonly summitZ = MASSIF_Z;

  constructor(readonly seed: number = Tuning.world.seed) {
    this.base = new Noise2D(seed);
    this.warp = new Noise2D(seed ^ 0x9e37);
    this.detail = new Noise2D(seed ^ 0x51ed);
    this.phase = new Noise2D(seed ^ 0x2f8b);
    this.rill = new Noise2D(seed ^ 0x7d41);
    this.aspect = new Noise2D(seed ^ 0x3ac1);
    this.netw = new Noise2D(seed ^ 0x1b7f);
    this.twist = new Noise2D(seed ^ 0x6d29);
    this.auditLandform();
  }

  /**
   * Measure the three landform invariants on the RAW mountain, once, at construction.
   *
   * Raw is the point: `overlay` is still null here, so this reads the heightfield the
   * trails module is about to carve into, not the carved result. Costs about 11k terrain
   * samples, ~4 ms, once at boot, against a 35 ms clipmap build.
   */
  private auditLandform(): void {
    const g = makeGroundSample();
    const M = AUDIT_RING_SAMPLES;
    const ring = new Float64Array(M);
    const fill = (R: number): void => {
      for (let i = 0; i < M; i++) {
        const a = (i / M) * Math.PI * 2;
        this.sample(MASSIF_X + R * Math.cos(a), MASSIF_Z + R * Math.sin(a), g);
        ring[i] = g.height;
      }
    };

    // --- channel convergence -------------------------------------------------------
    let near = 0;
    let far = 0;
    for (let b = 0; b < AUDIT_NEAR_R.length; b++) {
      fill(AUDIT_NEAR_R[b]);
      near += countChannels(ring, AUDIT_CHANNEL_PROMINENCE);
      fill(AUDIT_FAR_R[b]);
      far += countChannels(ring, AUDIT_CHANNEL_PROMINENCE);
    }
    CHANNEL_CONVERGENCE.observeWhere(
      near > 0 ? far / near : 0,
      `${(far / AUDIT_FAR_R.length).toFixed(1)} channels at R 2.4-2.8 km vs `
      + `${(near / AUDIT_NEAR_R.length).toFixed(1)} at R 0.6-0.8 km`,
    );

    // --- axisymmetry ---------------------------------------------------------------
    const meanOf = (R: number): number => {
      fill(R);
      let s = 0;
      for (let i = 0; i < M; i++) s += ring[i];
      return s / M;
    };
    const relief = meanOf(AUDIT_RELIEF_R0) - meanOf(AUDIT_RELIEF_R1);
    let dev2 = 0;
    for (const R of AUDIT_DEV_R) {
      fill(R);
      let mu = 0;
      for (let i = 0; i < M; i++) mu += ring[i];
      mu /= M;
      let v = 0;
      for (let i = 0; i < M; i++) v += (ring[i] - mu) * (ring[i] - mu);
      dev2 += v / M;
    }
    const dev = Math.sqrt(dev2 / AUDIT_DEV_R.length);
    AXISYMMETRY.observeWhere(
      relief > 1 ? dev / relief : 0,
      `${dev.toFixed(1)} m RMS of azimuthal shape against ${relief.toFixed(0)} m of radial relief`,
    );

    // --- basin budget --------------------------------------------------------------
    // A deterministic lattice, not random: this number has to be comparable between two
    // builds of the same world, and a different sample set every boot is a number that
    // moves on its own.
    let up = 0;
    let n = 0;
    let worstX = 0;
    let worstZ = 0;
    let worstUp = 0;
    for (let i = 0; i < AUDIT_BASIN_RINGS; i++) {
      const R = 900 + ((3200 - 900) * i) / (AUDIT_BASIN_RINGS - 1);
      for (let j = 0; j < AUDIT_BASIN_SPOKES; j++) {
        const a = (j / AUDIT_BASIN_SPOKES) * Math.PI * 2 + i * 0.113;
        const ux = Math.cos(a);
        const uz = Math.sin(a);
        const x = MASSIF_X + R * ux;
        const z = MASSIF_Z + R * uz;
        this.sample(x, z, g);
        // dh/dR = grad(h) . rHat, and grad(h) = (-nx, -nz)/ny.
        const dhdR = -(g.nx * ux + g.nz * uz) / (g.ny > 1e-6 ? g.ny : 1e-6);
        n++;
        if (dhdR > 0) {
          up++;
          if (dhdR > worstUp) { worstUp = dhdR; worstX = x; worstZ = z; }
        }
      }
    }
    BASIN.observeAt(n > 0 ? up / n : 0, worstX, this.height(worstX, worstZ), worstZ);

    // --- the spur band -------------------------------------------------------------
    // Same rings the axisymmetry number uses, split by azimuthal harmonic. See SPUR_BAND.
    let lowE = 0;
    let spurE = 0;
    let totE = 0;
    for (const R of AUDIT_DEV_R) {
      fill(R);
      let mu = 0;
      for (let i = 0; i < M; i++) mu += ring[i];
      mu /= M;
      for (let i = 0; i < M; i++) ring[i] -= mu;
      for (let k = 1; k <= 12; k++) {
        let ca = 0;
        let cb = 0;
        for (let i = 0; i < M; i++) {
          const th = (2 * Math.PI * k * i) / M;
          ca += ring[i] * Math.cos(th);
          cb += ring[i] * Math.sin(th);
        }
        // RMS of the k-th harmonic of a real signal: sqrt((a^2+b^2)/2) with a,b = 2/M sums.
        const e = (2 * (ca * ca + cb * cb)) / (M * M);
        if (k <= 3) lowE += e;
        if (k >= 5) spurE += e;
      }
      let v = 0;
      for (let i = 0; i < M; i++) v += ring[i] * ring[i];
      totE += v / M;
    }
    SPUR_BAND.observeWhere(
      totE > 0 ? spurE / totE : 0,
      `k=5-12 carries ${Math.sqrt(spurE / AUDIT_DEV_R.length).toFixed(1)} m RMS against `
      + `${Math.sqrt(lowE / AUDIT_DEV_R.length).toFixed(1)} m in k<=3`,
    );

    // --- the first harmonic ----------------------------------------------------------
    // Same rings again, k=1 alone. See K1_RELIEF for why it gets its own line.
    let k1E = 0;
    for (const R of AUDIT_DEV_R) {
      fill(R);
      let mu = 0;
      for (let i = 0; i < M; i++) mu += ring[i];
      mu /= M;
      let ca = 0;
      let cb = 0;
      for (let i = 0; i < M; i++) {
        const th = (2 * Math.PI * i) / M;
        ca += (ring[i] - mu) * Math.cos(th);
        cb += (ring[i] - mu) * Math.sin(th);
      }
      k1E += (2 * (ca * ca + cb * cb)) / (M * M);
    }
    const k1 = Math.sqrt(k1E / AUDIT_DEV_R.length);
    K1_RELIEF.observeWhere(
      k1,
      `${k1.toFixed(1)} m RMS at k=1 out of ${dev.toFixed(1)} m of azimuthal relief `
      + `(${((100 * k1 * k1) / (dev * dev)).toFixed(0)}% of its variance)`,
    );

    // --- the cap's amplitude ---------------------------------------------------------
    this.auditCap();
    // --- the twist's monotonicity ---------------------------------------------------
    this.auditTwist();
    // --- confluence -----------------------------------------------------------------
    this.auditConfluence();
    // --- the skyline ----------------------------------------------------------------
    this.auditSkyline(g);
  }

  /**
   * Smallest summit-block amplitude over the ring of bearings. See CAP_POSITIVE.
   *
   * Evaluated from the aspect noise instance the cap itself reads, on the same unit
   * direction, so it is the same number the hot path computes and not a bound on it. The
   * unit direction at radius CAP_R/2 is used: that is where the cap does most of its work,
   * and |dir| there is within 1% of 1 so the answer is the ring's, not the apex's.
   */
  private auditCap(): void {
    const R = CAP_R * 0.5;
    const kdir = R / Math.sqrt(R * R + DRAIN_CORE2);
    let worst = Infinity;
    let worstA = 0;
    for (let i = 0; i < AUDIT_RING_SAMPLES; i++) {
      const th = (i / AUDIT_RING_SAMPLES) * Math.PI * 2;
      const ax = Math.cos(th) * kdir;
      const az = Math.sin(th) * kdir;
      this.aspect.sample(ax * ASPECT2_SCALE + 27.91, az * ASPECT2_SCALE + 3.44, as3b);
      const a = CAP_AMP * (1 + CAP_TILT * (ax * TILT_CX + az * TILT_CZ) + CAP_LOBE * as3b[0]);
      if (a < worst) { worst = a; worstA = th; }
    }
    CAP_POSITIVE.observeWhere(
      worst,
      `min summit-block amplitude ${worst.toFixed(0)} m of ${CAP_AMP} m, on bearing `
      + `${((worstA * 180) / Math.PI).toFixed(0)} deg`,
    );
  }

  /**
   * THE SKYLINE. See SKYLINE_FLAT — this is the one landform audit that measures the
   * finished picture rather than a contour ring.
   *
   * A horizon ray-march: for each azimuth, the largest elevation angle any ground along it
   * subtends inside the far plane. That is exactly where terrain meets sky in the frame.
   * Then the range of that skyline across a 65-degree frame, for every (eye, bearing) pair
   * on a fixed lattice over the belt, and the share of those frames that come back inside
   * two degrees — a horizontal line.
   *
   * Cost: SKY_EYES * SKY_AZ * ~90 marched samples, about 15k terrain samples once at boot.
   * The lattice is fixed, never random: a number that has to be comparable between two
   * builds cannot be measured on a different sample set each time.
   */
  private auditSkyline(g: GroundSample): void {
    const M = SKY_AZ;
    const ang = new Float64Array(M);
    let flat = 0;
    let frames = 0;
    let worstRange = Infinity;
    let worstX = 0;
    let worstZ = 0;
    for (let e = 0; e < SKY_EYES; e++) {
      const ea = (e / SKY_EYES) * Math.PI * 2 + 0.31;
      const eR = SKY_EYE_R[e % SKY_EYE_R.length];
      const ex = MASSIF_X + eR * Math.cos(ea);
      const ez = MASSIF_Z + eR * Math.sin(ea);
      this.sample(ex, ez, g);
      const ey = g.height + SKY_EYE_UP;
      for (let i = 0; i < M; i++) {
        const a = (i / M) * Math.PI * 2;
        const dx = Math.cos(a);
        const dz = Math.sin(a);
        let best = -Infinity;
        for (let d = SKY_STEP0; d < SKY_FAR; d += SKY_STEP0 + SKY_STEPK * d) {
          this.sample(ex + dx * d, ez + dz * d, g);
          const t = (g.height - ey) / d;
          if (t > best) best = t;
        }
        ang[i] = (Math.atan(best) * 180) / Math.PI;
      }
      // Every 65-degree frame this eye could shoot, on the same azimuth lattice.
      const win = Math.max(2, Math.round((65 / 360) * M));
      for (let c = 0; c < M; c++) {
        let lo = Infinity;
        let hi = -Infinity;
        for (let k = 0; k < win; k++) {
          const v = ang[(c + k) % M];
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
        frames++;
        if (hi - lo < SKY_FLAT_DEG) flat++;
        if (hi - lo < worstRange) { worstRange = hi - lo; worstX = ex; worstZ = ez; }
      }
    }
    SKYLINE_FLAT.observeAt(
      frames > 0 ? flat / frames : 1,
      worstX,
      this.height(worstX, worstZ),
      worstZ,
    );
    SKYLINE_FLAT.noteWorst(
      `${flat} of ${frames} 65-deg framings from the belt have a skyline inside `
      + `${SKY_FLAT_DEG} deg; flattest ${worstRange.toFixed(2)} deg`,
    );
  }

  /**
   * Smallest azimuthal stretch of the twist map, over the belt. See TWIST_MONOTONE.
   *
   * dphi/dtheta is taken from the noise field's own analytic gradient, not from a
   * difference of two samples: this is checking a bound on a derivative and a
   * finite-difference version of it would be checking a bound on something else.
   */
  private auditTwist(): void {
    let worst = Infinity;
    let worstR = 0;
    let worstA = 0;
    for (let R = 400; R <= 4000; R += 50) {
      const rs = Math.sqrt(R * R + DRAIN_CORE2);
      const kdir = R / rs;
      smoothstepD(TWIST_R0, TWIST_R1, R, ss2);
      const ramp = ss2[0];
      for (let i = 0; i < 512; i++) {
        const th = (i / 512) * Math.PI * 2;
        const c = Math.cos(th);
        const s = Math.sin(th);
        this.twist.sample(c * kdir * TWIST_SCALE + 13.77, s * kdir * TWIST_SCALE + 41.93, tw3);
        // d(unit direction)/dtheta = kdir * (-sin, cos)
        const dwdth = TWIST_SCALE * kdir * (-s * tw3[1] + c * tw3[2]);
        const u = TWIST * ramp * tw3[0];
        const j = 1 + (2 / (1 + u * u)) * TWIST * ramp * dwdth;
        if (j < worst) { worst = j; worstR = R; worstA = th; }
      }
    }
    TWIST_MONOTONE.observeAt(
      worst,
      MASSIF_X + worstR * Math.cos(worstA),
      0,
      MASSIF_Z + worstR * Math.sin(worstA),
    );
  }

  /**
   * Do channels actually join, or do they only get further apart? See CONFLUENCE.
   *
   * This is the in-engine copy of the tracer in captures/w5-terrain-r8-tools/measure.mjs,
   * at a coarser radial step so it costs 12 ms rather than 60. The probe version is the
   * one with the synthetic-field selftest behind it; this one is checked against it on the
   * shipped build, which is the only honest way to run a cheap copy of an instrument.
   */
  private auditConfluence(): void {
    const M = AUDIT_TRACE_M;
    const g = makeGroundSample();
    const ring = new Float64Array(M);
    const fill = (R: number): void => {
      for (let i = 0; i < M; i++) {
        const a = (i / M) * Math.PI * 2;
        this.sample(MASSIF_X + R * Math.cos(a), MASSIF_Z + R * Math.sin(a), g);
        ring[i] = g.height;
      }
      let mu = 0;
      for (let i = 0; i < M; i++) mu += ring[i];
      mu /= M;
      for (let i = 0; i < M; i++) ring[i] -= mu;
      for (let k = 1; k <= AUDIT_DETREND_K; k++) {
        let ca = 0;
        let cb = 0;
        for (let i = 0; i < M; i++) {
          const th = (2 * Math.PI * k * i) / M;
          ca += ring[i] * Math.cos(th);
          cb += ring[i] * Math.sin(th);
        }
        ca = (2 * ca) / M;
        cb = (2 * cb) / M;
        for (let i = 0; i < M; i++) {
          const th = (2 * Math.PI * k * i) / M;
          ring[i] -= ca * Math.cos(th) + cb * Math.sin(th);
        }
      }
    };
    /** Highest ground strictly between two ring indices, above the higher of two floors. */
    const divide = (i: number, j: number, hi: number, hj: number): number => {
      let mx = -Infinity;
      for (let k = (i + 1) % M; k !== j; k = (k + 1) % M) if (ring[k] > mx) mx = ring[k];
      return mx - (hi > hj ? hi : hj);
    };

    fill(AUDIT_TRACE_R0);
    const seed: number[] = [];
    for (let i = 0; i < M; i++) {
      const b = ring[i];
      if (!(b < ring[(i - 1 + M) % M] && b <= ring[(i + 1) % M])) continue;
      let maxL = b;
      let okL = false;
      for (let k = 1; k < M; k++) {
        const v = ring[(i - k + M) % M];
        if (v < b) { okL = true; break; }
        if (v > maxL) maxL = v;
      }
      let maxR = b;
      let okR = false;
      for (let k = 1; k < M; k++) {
        const v = ring[(i + k) % M];
        if (v < b) { okR = true; break; }
        if (v > maxR) maxR = v;
      }
      let prom: number;
      if (okL && okR) prom = (maxL < maxR ? maxL : maxR) - b;
      else if (okL) prom = maxL - b;
      else if (okR) prom = maxR - b;
      else prom = (maxL > maxR ? maxL : maxR) - b;
      if (prom >= AUDIT_CHANNEL_PROMINENCE) seed.push(i);
    }
    const N = seed.length;
    if (N < 4) {
      CONFLUENCE.observeWhere(0, `only ${N} channels found at R = ${AUDIT_TRACE_R0} m to trace`);
      return;
    }
    const idx = seed.slice();
    const alive = seed.map(() => true);
    const hAt = new Float64Array(N);
    const D0 = new Float64Array(N);
    for (let i = 0; i < N; i++) {
      const j = (i + 1) % N;
      D0[i] = divide(idx[i], idx[j], ring[idx[i]], ring[idx[j]]);
    }
    let merges = 0;
    let deaths = 0;
    for (let R = AUDIT_TRACE_R0 + AUDIT_TRACE_DR; R <= AUDIT_TRACE_R1; R += AUDIT_TRACE_DR) {
      fill(R);
      const arc = (2 * Math.PI * R) / M;
      const win = Math.max(1, Math.round((AUDIT_TRACE_BEND * AUDIT_TRACE_DR) / arc));
      for (let t = 0; t < N; t++) {
        if (!alive[t]) continue;
        let best = idx[t];
        let bestH = Infinity;
        for (let s = -win; s <= win; s++) {
          const q = (idx[t] + s + M) % M;
          if (ring[q] < bestH) { bestH = ring[q]; best = q; }
        }
        idx[t] = best;
        hAt[t] = bestH;
      }
      const order: number[] = [];
      for (let t = 0; t < N; t++) if (alive[t]) order.push(t);
      if (order.length < 3) break;
      order.sort((p, q) => idx[p] - idx[q]);
      const nO = order.length;
      const dv = new Float64Array(nO);
      for (let o = 0; o < nO; o++) {
        const i = order[o];
        const j = order[(o + 1) % nO];
        let sep = idx[j] - idx[i];
        if (sep < 0) sep += M;
        dv[o] = sep * arc < AUDIT_MERGE_ARC ? 0 : divide(idx[i], idx[j], hAt[i], hAt[j]);
      }
      for (let o = 0; o < nO; o++) {
        const i = order[o];
        const j = order[(o + 1) % nO];
        if (!alive[i] || !alive[j]) continue;
        const thresh = 0.12 * D0[i] > 2 ? 0.12 * D0[i] : 2;
        if (dv[o] >= thresh) continue;
        // A CONFLUENCE only if both were still real channels when they met — each with a
        // divide of its own on its far side. Without that test a channel that merely faded
        // out scores as a join, which is exactly the reading R7 would have passed on.
        const outI = dv[(o - 1 + nO) % nO];
        const outJ = dv[(o + 1) % nO];
        if (outI >= AUDIT_CHANNEL_PROMINENCE && outJ >= AUDIT_CHANNEL_PROMINENCE && D0[i] >= 8) merges++;
        else deaths++;
        alive[j] = false;
        if (D0[j] > D0[i]) D0[i] = D0[j];
      }
    }
    let survivors = 0;
    for (const a of alive) if (a) survivors++;
    CONFLUENCE.observeWhere(
      merges / N,
      `${merges} of ${N} channels join another between R 1.2 and 2.9 km; `
      + `${deaths} died out, ${survivors} still distinct at the runout`,
    );
  }

  setTrailOverlay(o: TrailOverlay | null): void {
    this.overlay = o;
  }

  /** Cheap height-only query. */
  height(x: number, z: number): number {
    const s = this.acquire();
    this.sample(x, z, s);
    const h = s.height;
    this.release();
    return h;
  }

  /**
   * Full ground query: height, exact normal, surface material, roughness.
   * Hot path — called several times per wheel per sim step, so it allocates nothing.
   */
  sample(x: number, z: number, out: GroundSample): GroundSample {
    const px0 = x - MASSIF_X;
    const pz0 = z - MASSIF_Z;
    const r2 = px0 * px0 + pz0 * pz0;
    const R = Math.sqrt(r2);
    const Rsafe = R > 1 ? R : 1;

    // ---- 0a. the twist ------------------------------------------------------------
    // Rotate the query about the summit by phi = 2*atan(u), u = TWIST * ramp(R) * w(dir).
    // A rotation preserves R, so every radial ramp downstream is untouched and the whole
    // of the rest of this function runs unchanged in the rotated frame; only the final
    // gradient has to be brought back. See TWIST for why this cannot open a basin.
    const rs2 = r2 + DRAIN_CORE2;
    const rs = Math.sqrt(rs2);
    const irs = 1 / rs;
    const irs3 = irs / rs2;
    // Unit direction BEFORE the rotation, and its exact derivatives. The twist has to be
    // a function of the incoming azimuth or it is an implicit equation.
    const bx = px0 * irs;
    const bz = pz0 * irs;
    const dbxdx = (rs2 - px0 * px0) * irs3;
    const dbxdz = -px0 * pz0 * irs3;
    const dbzdz = (rs2 - pz0 * pz0) * irs3;
    this.twist.sample(bx * TWIST_SCALE + 13.77, bz * TWIST_SCALE + 41.93, tw3);
    smoothstepD(TWIST_R0, TWIST_R1, R, ss2f);
    const twRamp = ss2f[0];
    const twW = tw3[0];
    const dtwWx = TWIST_SCALE * (tw3[1] * dbxdx + tw3[2] * dbxdz);
    const dtwWz = TWIST_SCALE * (tw3[1] * dbxdz + tw3[2] * dbzdz);
    const u = TWIST * twRamp * twW;
    const ux = TWIST * (twRamp * dtwWx + ss2f[1] * (px0 / Rsafe) * twW);
    const uz = TWIST * (twRamp * dtwWz + ss2f[1] * (pz0 / Rsafe) * twW);
    // Exact cos/sin of 2*atan(u) — no transcendental, and |phi| < pi by construction.
    const den = 1 / (1 + u * u);
    const cT = (1 - u * u) * den;
    const sT = 2 * u * den;
    const dcdu = -4 * u * den * den;
    const dsdu = 2 * (1 - u * u) * den * den;
    const px = cT * px0 - sT * pz0;
    const pz = sT * px0 + cT * pz0;
    // d(rotated)/d(world). Not a rotation: u varies with position, so this carries the
    // shear that does the actual work.
    const q00 = cT + (dcdu * px0 - dsdu * pz0) * ux;
    const q01 = -sT + (dcdu * px0 - dsdu * pz0) * uz;
    const q10 = sT + (dsdu * px0 + dcdu * pz0) * ux;
    const q11 = cT + (dsdu * px0 + dcdu * pz0) * uz;

    // dR/dx, dR/dz in the rotated frame.
    const rux = px / Rsafe;
    const ruz = pz / Rsafe;

    // ---- 0. aspect: the profile's own radius depends on direction ----------------
    // Reff = R * s, s = 1 + ASPECT_AMP * a(dir). `a` is sampled on the unit direction
    // vector (px, pz)/rs, which is a smooth periodic function of azimuth with no atan2
    // and no branch cut, and which collapses to a constant at the apex (|dir| -> 0), so
    // the summit dome stays a body of revolution.
    //
    // s is a function of DIRECTION ONLY, so dh/dR = P'(Reff) * s has exactly the sign
    // P' has, everywhere. That is why this term is allowed to be large: it cannot open a
    // basin at any amplitude, which is not true of any relief field. See ASPECT_AMP.
    const ax = px * irs;
    const az = pz * irs;
    const daxdx = (rs2 - px * px) * irs3;
    const daxdz = -px * pz * irs3;
    const dazdz = (rs2 - pz * pz) * irs3;
    this.aspect.sample(ax * ASPECT_SCALE + 4.31, az * ASPECT_SCALE + 8.77, as3);
    // Second octave, ~9 lobes: the spurs and the valleys between them. Same field, same
    // guarantee — a function of direction cannot open a basin. See ASPECT2_SCALE.
    this.aspect.sample(ax * ASPECT2_SCALE + 27.91, az * ASPECT2_SCALE + 3.44, as3b);
    // THE TILT: (dir . axis) is cos(theta - theta0) exactly, i.e. pure k=1, the one band
    // no noise circle can put energy in. Minus, so the high side is the +TILT_C bearing.
    // See TILT_AMP for why k=1 is the cheapest azimuthal relief in the file.
    const dTilt = ax * TILT_CX + az * TILT_CZ;
    const dsx = ASPECT_AMP * ASPECT_SCALE * (as3[1] * daxdx + as3[2] * daxdz)
      + ASPECT2_AMP * ASPECT2_SCALE * (as3b[1] * daxdx + as3b[2] * daxdz)
      - TILT_AMP * (TILT_CX * daxdx + TILT_CZ * daxdz);
    const dsz = ASPECT_AMP * ASPECT_SCALE * (as3[1] * daxdz + as3[2] * dazdz)
      + ASPECT2_AMP * ASPECT2_SCALE * (as3b[1] * daxdz + as3b[2] * dazdz)
      - TILT_AMP * (TILT_CX * daxdz + TILT_CZ * dazdz);
    const sAsp = 1 + ASPECT_AMP * as3[0] + ASPECT2_AMP * as3b[0] - TILT_AMP * dTilt;
    const Re = R * sAsp;
    const dRex = rux * sAsp + R * dsx;
    const dRez = ruz * sAsp + R * dsz;

    // ---- 1. radial massif profile -----------------------------------------------
    profile(Re, prof2);
    let h = prof2[0];
    let dhx = prof2[1] * dRex;
    let dhz = prof2[1] * dRez;

    // ---- 1b. the summit block ----------------------------------------------------
    // hcap = A(dir) * (1-t)^CAP_N * (1 + CAP_N*t), t = R/CAP_R clamped at 1. Monotone
    // non-increasing in R for A > 0, so it can only steepen the descent and cannot open a
    // basin at any amplitude; zero with zero slope at t = 1, so the profile outside CAP_R
    // is untouched to the bit, which is what keeps the whole trail network out of it.
    // R is invariant under the twist, so this is the same in either frame.
    if (R < CAP_R) {
      const t = R / CAP_R;
      const om = 1 - t;
      const om2 = om * om;
      const capA = CAP_AMP * (1 + CAP_TILT * dTilt + CAP_LOBE * as3b[0]);
      // shape and its derivative w.r.t. t
      // CAP_N is 3 and the closed form below is written out for 3: (1-t)^3*(1+3t) and
      // its derivative -12*t*(1-t)^2. Changing CAP_N means changing these two lines, and
      // the CAP_SHAPE invariant checks the pair against the definition at build time.
      const sh = om2 * om * (1 + 3 * t);
      const dsh = -12 * t * om2;
      h += capA * sh;
      // dA/d(rotated x,z) — direction-only, exactly like the aspect scale above.
      const dAx = CAP_AMP * (CAP_TILT * (TILT_CX * daxdx + TILT_CZ * daxdz)
        + CAP_LOBE * ASPECT2_SCALE * (as3b[1] * daxdx + as3b[2] * daxdz));
      const dAz = CAP_AMP * (CAP_TILT * (TILT_CX * daxdz + TILT_CZ * dazdz)
        + CAP_LOBE * ASPECT2_SCALE * (as3b[1] * daxdz + as3b[2] * dazdz));
      const capRad = (capA * dsh) / CAP_R;
      dhx += capRad * rux + dAx * sh;
      dhz += capRad * ruz + dAz * sh;
    }

    // ---- 1c. the outliers --------------------------------------------------------
    // Hills beyond the foot of the massif, so the far skyline is a far side of a valley
    // rather than the profile's own contour. Gated on BOTH the world radius and Reff —
    // see FOOT_AMP. smoothstep is zero in value and slope at its lower edge, so skipping
    // the whole block below FOOT_R0 is exact and the near field pays nothing for it.
    if (R > FOOT_R0) {
      smoothstepD(FOOT_R0, FOOT_R1, R, ss2g);
      smoothstepD(FOOT_RE0, FOOT_RE1, Re, ss2h);
      const gA = ss2g[0] * ss2h[0];
      if (gA > 0) {
        const fi = 1 / FOOT_SCALE;
        fbm(this.warp, px * fi + 63.1, pz * fi + 19.7, 2, 2.03, 0.5, ft3);
        const fv = ft3[0];
        h += FOOT_AMP * gA * fv * fv;
        const dgx = ss2g[1] * rux * ss2h[0] + ss2g[0] * ss2h[1] * dRex;
        const dgz = ss2g[1] * ruz * ss2h[0] + ss2g[0] * ss2h[1] * dRez;
        const two = 2 * FOOT_AMP * gA * fv * fi;
        dhx += FOOT_AMP * dgx * fv * fv + two * ft3[1];
        dhz += FOOT_AMP * dgz * fv * fv + two * ft3[2];
      }
    }

    // ---- 2. drainage-aligned domain map -----------------------------------------
    // q = p * k(R), k = (DRAIN_REF / Rs)^(1-rho), Rs = sqrt(R^2 + core^2).
    //
    // dk/dx = (rho-1) * k * px / Rs^2, so the Jacobian is the symmetric
    //     J = k * [ 1 + e*px*px    e*px*pz  ]      with e = (rho-1)/Rs^2 .
    //             [ e*px*pz        1 + e*pz*pz ]
    // Contract it with the unit tangent (-pz, px)/R and the cross terms cancel exactly:
    // the tangential scale is k. Contract with the unit radial (px, pz)/R and it gives
    // k*(1 + e*R^2) -> k*rho for R >> core. That exact rho:1 anisotropy is the slope
    // budget; everything downstream inherits it.
    const k2 = Math.sqrt(DRAIN_REF / rs);
    const k4 = Math.sqrt(k2);
    const k8 = Math.sqrt(k4);
    const k16 = Math.sqrt(k8);
    const k = k2 * k4 * k8; // (ref/rs)^(1/2+1/4+1/8) = ^0.875, i.e. rho = 0.125
    const e = (DRAIN_RHO - 1) / rs2;
    const kpx = k * px;
    const kpz = k * pz;
    const j00 = k + e * kpx * px;
    const j01 = e * kpx * pz;
    const j11 = k + e * kpz * pz;
    const qx = kpx;
    const qz = kpz;

    // ---- 3. ridged multifractal in the mapped domain ----------------------------
    const iw = 1 / WARP_SCALE;
    const inv = 1 / DRAIN_SCALE;
    this.warp.sample(qx * iw, qz * iw, w3);
    this.warp.sample(qx * iw + 11.7, qz * iw + 3.9, v3);
    const wx = qx * inv + WARP_AMP * w3[0];
    const wz = qz * inv + WARP_AMP * v3[0];
    const a00 = inv + WARP_AMP * w3[1] * iw;
    const a01 = WARP_AMP * w3[2] * iw;
    const a10 = WARP_AMP * v3[1] * iw;
    const a11 = inv + WARP_AMP * v3[2] * iw;

    ridgedMF(this.base, wx, wz, DRAIN_OCTAVES, DRAIN_LAC, DRAIN_GAIN, DRAIN_EROSION, t3, RIDGE_ROUND);
    const rv = t3[0];
    // d/dq through the warp, then d/dp through the drainage Jacobian.
    const rqx = t3[1] * a00 + t3[2] * a10;
    const rqz = t3[1] * a01 + t3[2] * a11;
    const rdx = rqx * j00 + rqz * j01;
    const rdz = rqx * j01 + rqz * j11;

    // Amplitude: alpine near the peak, moderate on the mid-mountain, and a low floor
    // out on the valley plain so the bottom of the world is not a mirror.
    //
    // The ramps are fed Reff, not R: the relief has to fade where the profile actually
    // flattens, which is now a different radius on every aspect. Feed them R and the
    // valley fade cuts a ring of flat relief across a flank that is still descending.
    smoothstepD(DRAIN_AMP_R0, DRAIN_AMP_R1, Re, ss2);
    smoothstepD(DRAIN_FADE_R0, DRAIN_FADE_R1, Re, ss2b);
    const ampR = DRAIN_AMP_HIGH + (DRAIN_AMP_LOW - DRAIN_AMP_HIGH) * ss2[0];
    const dAmpR = (DRAIN_AMP_LOW - DRAIN_AMP_HIGH) * ss2[1];
    const fade = 1 - ss2b[0] * (1 - DRAIN_AMP_FLOOR / DRAIN_AMP_LOW);
    const dFade = -ss2b[1] * (1 - DRAIN_AMP_FLOOR / DRAIN_AMP_LOW);
    const amp = ampR * fade;
    const dAmp = dAmpR * fade + ampR * dFade;
    const rc = rv - DRAIN_MID;
    h += amp * rc;
    dhx += amp * rdx + dAmp * dRex * rc;
    dhz += amp * rdz + dAmp * dRez * rc;

    // ---- 4. secondary drainages -------------------------------------------------
    // Evaluated in the *mapped* domain, not in world space. An isotropic field here
    // is the one thing that can still open a basin: it points uphill as readily as
    // down, at full strength, and once the main relief's trough floors are flattened
    // by the erosion term there is nothing left to out-vote it. Through the drainage
    // Jacobian this field inherits the same rho:1 budget as everything else — it can
    // carve a 20 m side gully across the fall line while contributing an eighth of
    // that along it.
    const bi = 1 / BENCH_SCALE;
    fbm(this.detail, qx * bi + 17.3, qz * bi + 5.1, 3, 2.11, 0.5, t3);
    const benchV = t3[0];
    const bqx = t3[1] * bi;
    const bqz = t3[2] * bi;
    const benchDx = bqx * j00 + bqz * j01;
    const benchDz = bqx * j01 + bqz * j11;
    // ss2 was consumed by the drainage amplitude ramp above and is free to reuse.
    smoothstepD(BENCH_GROW_R0, BENCH_GROW_R1, Re, ss2);
    const bAmp = BENCH_AMP + (BENCH_AMP_FAR - BENCH_AMP) * ss2[0];
    const dbAmp = (BENCH_AMP_FAR - BENCH_AMP) * ss2[1];
    h += bAmp * benchV;
    dhx += bAmp * benchDx + dbAmp * dRex * benchV;
    dhz += bAmp * benchDz + dbAmp * dRez * benchV;

    // ---- 4b. the drainage network: an annulus trace ------------------------------
    // See NET_SCALE for what an annulus trace is and NET_M for why it is the only thing
    // in this file that can make channel count FALL downhill.
    //
    // Q = p * gN / NET_SCALE puts the ring of ground at radius R on a circle of noise-
    // space radius R * gN / NET_SCALE, and gN = (ref/rs)^(1+NET_M) shrinks that circle as
    // R grows. Fewer features crossed per revolution, at every amplitude and every depth
    // threshold: channels converge because the DOMAIN converges, not because a weight
    // was tuned. 1 + 3/16 = 1 + 1/8 + 1/16, so gN is (ref/rs) * k8 * k16 and costs no
    // square root the drainage map had not already built.
    const gN = k2 * k2 * k8;
    const egn = -(1 + NET_M) / rs2;
    const cN = gN / NET_SCALE;
    // d(gN)/dx = egn * gN * px, so the trace's own Jacobian is symmetric, like the
    // drainage map's, and for the same reason.
    const g00 = cN * (1 + egn * px * px);
    const g01 = cN * egn * px * pz;
    const g11 = cN * (1 + egn * pz * pz);

    // AZIMUTHAL WARP. Displacing the trace point along the unit tangent moves a channel
    // sideways by NET_SCALE * W / gN metres. The bending is the twist's job now; what is
    // left here is the long drainage warp, which modulates channel SPACING around the ring
    // so a field this regular does not read as corduroy. See NET_SPREAD.
    const gwx = w3[1] * iw;
    const gwz = w3[2] * iw;
    const wl = NET_SPREAD * w3[0];
    const dwlx = NET_SPREAD * (gwx * j00 + gwz * j01);
    const dwlz = NET_SPREAD * (gwx * j01 + gwz * j11);
    // THE MELT. The network's amplitude collapses where |w| is small, which is exactly
    // where the twist is squeezing channels together, so the divide between two converging
    // channels goes away as they meet. Sampled on the ROTATED direction, not the incoming
    // one: every derivative from here to the end of the network block lives in the rotated
    // frame, and mixing frames inside a product rule is a wrong normal, not a small error.
    // The rotation is under 12 degrees against a field that varies over 55, so it is the
    // same lobe and the correlation with the compression survives.
    this.netw.sample(ax * TWIST_SCALE + 13.77, az * TWIST_SCALE + 41.93, nw3);
    const meltW = nw3[0];
    const dmwx = TWIST_SCALE * (nw3[1] * daxdx + nw3[2] * daxdz);
    const dmwz = TWIST_SCALE * (nw3[1] * daxdz + nw3[2] * dazdz);
    const mq = 1 + NET_MELT_K * meltW * meltW;
    const imq = 1 / mq;
    // Ramped with radius on the same schedule as the twist: full relief in the alpine head
    // where nothing has converged yet, melted out where it has.
    const meltA = NET_MELT * twRamp;
    const melt = 1 - meltA * imq;
    const dmeltK = 2 * meltA * NET_MELT_K * meltW * imq * imq;
    const dmeltx = dmeltK * dmwx - NET_MELT * ss2f[1] * rux * imq;
    const dmeltz = dmeltK * dmwz - NET_MELT * ss2f[1] * ruz * imq;
    // Unit tangent (-pz, px)/rs and its exact derivatives. Softened by rs rather than R
    // so it is finite at the apex.
    const tgx = -pz * irs;
    const tgz = px * irs;
    const dtgxdx = px * pz * irs3;
    const dtgxdz = -(rs2 - pz * pz) * irs3;
    const dtgzdx = (rs2 - px * px) * irs3;
    const dtgzdz = -px * pz * irs3;

    const qnx = px * cN + wl * tgx;
    const qnz = pz * cN + wl * tgz;
    // Not symmetric any more: the warp is a general displacement field.
    const m00 = g00 + dwlx * tgx + wl * dtgxdx;
    const m01 = g01 + dwlz * tgx + wl * dtgxdz;
    const m10 = g01 + dwlx * tgz + wl * dtgzdx;
    const m11 = g11 + dwlz * tgz + wl * dtgzdz;

    // TRUNKS. Subtracted, so the ridged field's crease is the floor of a channel (concave
    // — a compression, not a launch) and its broad minimum is the interfluve. Amplitude
    // NET_AMP / gN holds the cross-slope gradient exactly constant at every radius.
    ridgedMF(this.rill, qnx + 3.17, qnz + 29.71, NET_OCTAVES, NET_LAC, NET_GAIN, NET_EROSION, t3, NET_ROUND);
    const trunkF = t3[0];
    const trunkV = trunkF - NET_MID;
    const trunkDx = t3[1] * m00 + t3[2] * m10;
    const trunkDz = t3[1] * m01 + t3[2] * m11;
    // The dissection ramp. Both generations get steeper toward the alpine head and
    // gentler out where the riding is: it is what a real massif does, it puts the block's
    // curvature and its radial-gradient leak where nothing rides, and it is what makes
    // the channel count at R=700 large enough to fall to the count at R=2600.
    smoothstepD(NET_DISS_R0, NET_DISS_R1, R, ss2e);
    const diss = 1 - ss2e[0];
    const dDiss = -ss2e[1];
    const nGrad = NET_GRAD_LO + (NET_GRAD_HI - NET_GRAD_LO) * diss;
    const dnGrad = (NET_GRAD_HI - NET_GRAD_LO) * dDiss;
    const nScale = NET_SCALE / gN;
    const nAmp0 = nGrad * nScale;
    const nAmp = nAmp0 * melt;
    // d(1/gN)/dx = -egn * (1/gN) * px, plus the ramp's own term, times the melt, plus the
    // melt's own term. Written as a product rule rather than folded so that setting
    // NET_MELT to zero ablates it exactly.
    const dnAmpx = melt * (-egn * nAmp0 * px + dnGrad * rux * nScale) + nAmp0 * dmeltx;
    const dnAmpz = melt * (-egn * nAmp0 * pz + dnGrad * ruz * nScale) + nAmp0 * dmeltz;
    h -= nAmp * trunkV;
    dhx -= nAmp * trunkDx + dnAmpx * trunkV;
    dhz -= nAmp * trunkDz + dnAmpz * trunkV;

    // TRIBUTARIES. The same trace at NT times the wavenumber, gated to the interfluves so
    // they run down the spur flanks and die out where they meet a trunk floor. NT falls
    // with radius, so the two generations converge toward each other going downhill —
    // which is what a tributary joining a trunk is, and what takes the measured channel
    // count ratio below what the map alone gives.
    smoothstepD(NET_NT_R0, NET_NT_R1, R, ss2c);
    const nt = NET_NT_HI + (NET_NT_LO - NET_NT_HI) * ss2c[0];
    const dnt = (NET_NT_LO - NET_NT_HI) * ss2c[1];
    const p00 = m00 * nt + qnx * dnt * rux;
    const p01 = m01 * nt + qnx * dnt * ruz;
    const p10 = m10 * nt + qnz * dnt * rux;
    const p11 = m11 * nt + qnz * dnt * ruz;
    ridgedMF(this.rill, qnx * nt + 71.9, qnz * nt + 13.3, NET_OCTAVES, NET_LAC, NET_GAIN, NET_EROSION, tr3, NET_ROUND);
    const tribV = tr3[0] - NET_MID;
    const tribDx = tr3[1] * p00 + tr3[2] * p10;
    const tribDz = tr3[1] * p01 + tr3[2] * p11;
    // Gate: 1 on the interfluve (trunk field low), 0 in the trunk floor (field at its
    // crease, 1). ss2d[1] is dS/d(trunkF), carried exactly.
    smoothstepD(NET_GATE_LO, NET_GATE_HI, trunkF, ss2d);
    const gate = 1 - ss2d[0];
    const dgatex = -ss2d[1] * trunkDx;
    const dgatez = -ss2d[1] * trunkDz;
    // Amplitude = gradient(R) * spacing, and the gradient RAMPS DOWN with radius. See
    // NET_TRIB_GRAD_HI: without the ramp this generation switches itself on going
    // downhill and cancels the trunk's convergence exactly.
    const tGrad = NET_TRIB_GRAD_LO + (NET_TRIB_GRAD_HI - NET_TRIB_GRAD_LO) * diss;
    const dtGrad = (NET_TRIB_GRAD_HI - NET_TRIB_GRAD_LO) * dDiss;
    const tScale = nScale / nt;
    const tAmp0 = tGrad * tScale;
    const tAmp = tAmp0 * melt;
    // Written as a sum rather than as tAmp * (... + dtGrad/tGrad) on purpose: the second
    // form divides by the gradient, and an ablation that sets it to zero then reports NaN
    // instead of "this term contributes nothing". A probe that cannot be ablated is a
    // probe that cannot be checked.
    const dtAmpx = melt * (tAmp0 * (-egn * px - (dnt * rux) / nt) + dtGrad * rux * tScale)
      + tAmp0 * dmeltx;
    const dtAmpz = melt * (tAmp0 * (-egn * pz - (dnt * ruz) / nt) + dtGrad * ruz * tScale)
      + tAmp0 * dmeltz;
    h -= tAmp * gate * tribV;
    dhx -= tAmp * (dgatex * tribV + gate * tribDx) + dtAmpx * gate * tribV;
    dhz -= tAmp * (dgatez * tribV + gate * tribDz) + dtAmpz * gate * tribV;

    // ---- 4c. back to world coordinates -------------------------------------------
    // Everything above was evaluated at the rotated point, so dh so far is d/d(rotated).
    // Everything below (the terrace phase, the chatter) is world-space by design and must
    // not go through this. h itself is a scalar and needs no transform.
    let dhwx = dhx * q00 + dhz * q10;
    let dhwz = dhx * q01 + dhz * q11;

    // ---- 5. terrace into benches ------------------------------------------------
    // h' = lerp(q, quantise(q), W) where q is the height plus a wandering phase.
    // Without the phase the benches land at the same absolute elevation everywhere,
    // and every slope-driven thing downstream — rock classification especially —
    // comes out as dead-straight contour stripes running round the whole mountain.
    //
    // The phase has to be *isotropic*, and this is the one place in the function where
    // that is not only safe but required. R3 first drove the phase from the mid-scale
    // field, which is 8:1 elongated downhill: its value barely changes as you descend a
    // gully, so the benches came out at a fixed elevation interval down every line and
    // the face read as a flight of stairs. A world-space field breaks the interval both
    // across and along the fall line, so benches wander, merge and pinch out the way
    // real ones do. It is safe because the phase only ever reaches the height through
    // the terrace factor W*(dS-1), which averages to zero over a period — it cannot
    // bias the fall line the way a term added straight into h would. Its worst-case
    // gradient contribution is +-0.05.
    //
    // It reuses the noise instance the old (unused) rock mask allocated, keeping the same
    // seed, so this costs one lattice evaluation and no new memory.
    const pi = 1 / TERRACE_PHASE_SCALE;
    this.phase.sample(x * pi + 5.7, z * pi + 12.1, v3);
    {
      const q = h + TERRACE_JITTER * v3[0];
      const dqx = dhwx + TERRACE_JITTER * v3[1] * pi;
      const dqz = dhwz + TERRACE_JITTER * v3[2] * pi;
      const t = q / TERRACE_PERIOD;
      const kq = Math.floor(t);
      const f = t - kq;
      // Asymmetric quantiser: S'(f) = 12 f (1-f)^2, so S = 3f^4 - 8f^3 + 6f^2. See
      // TERRACE_PERIOD. Convex side (the nose of the roll, f > 1/3 descending) peaks at
      // |S''| = 4 instead of the symmetric quintic's 5.77; the concave side takes the
      // difference. dS stays in [0, 1.778], so the gradient factor 1 + W*(dS-1) is still
      // strictly positive and the terrace still cannot invert a slope or open a basin.
      const om = 1 - f;
      const S = f * f * (6 + f * (3 * f - 8));
      const dS = 12 * f * om * om;
      h += TERRACE_WEIGHT * (TERRACE_PERIOD * (kq + S) - q);
      dhwx += TERRACE_WEIGHT * (dS - 1) * dqx;
      dhwz += TERRACE_WEIGHT * (dS - 1) * dqz;
    }

    // ---- 6. chatter -------------------------------------------------------------
    const ci = 1 / CHATTER_SCALE;
    fbm(this.detail, x * ci + 91.3, z * ci + 47.9, 3, 2.17, 0.5, t3);
    h += CHATTER_AMP * t3[0];
    dhwx += CHATTER_AMP * t3[1] * ci;
    dhwz += CHATTER_AMP * t3[2] * ci;

    out.height = h;
    // Normal of y = h(x,z) is normalize(-dh/dx, 1, -dh/dz).
    const nx = -dhwx;
    const nz = -dhwz;
    const len = Math.sqrt(nx * nx + 1 + nz * nz);
    out.nx = nx / len;
    out.ny = 1 / len;
    out.nz = nz / len;

    const slope = Math.sqrt(dhwx * dhwx + dhwz * dhwz);
    out.trailWeight = 0;
    out.surface = classify(h, slope, rv, benchV);
    out.roughness = ROUGHNESS[out.surface] + slope * 0.02;

    if (this.overlay) this.overlay.apply(x, z, out);
    return out;
  }

  /** Steepest-descent direction at a point — used to route trails downhill. */
  descent(x: number, z: number, out: { x: number; z: number }): void {
    const s = this.acquire();
    this.sample(x, z, s);
    const l = Math.hypot(s.nx, s.nz) || 1;
    out.x = s.nx / l;
    out.z = s.nz / l;
    this.release();
  }

  /** Ray-march the heightfield. Returns distance along dir or -1. Used by camera collision. */
  raymarch(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): number {
    let t = 0;
    let prevGap = oy - this.height(ox, oz);
    const step = 0.35;
    while (t < maxDist) {
      t += step;
      const px = ox + dx * t;
      const py = oy + dy * t;
      const pz = oz + dz * t;
      const gap = py - this.height(px, pz);
      if (gap <= 0) {
        // Linear refine between the last two samples.
        const f = prevGap / (prevGap - gap || 1);
        return t - step + step * clamp01(f);
      }
      prevGap = gap;
    }
    return -1;
  }

  // A trail overlay is allowed to query the terrain from inside apply(), so the
  // internal scratch has to be a stack rather than one shared object.
  private readonly pool: GroundSample[] = [
    makeGroundSample(), makeGroundSample(), makeGroundSample(), makeGroundSample(),
  ];
  private depth = 0;

  private acquire(): GroundSample {
    const i = this.depth < this.pool.length ? this.depth : this.pool.length - 1;
    this.depth++;
    return this.pool[i];
  }

  private release(): void {
    if (this.depth > 0) this.depth--;
  }
}

/**
 * Surface material. Driven by absolute elevation (snow and treeline are real
 * altitudes, not fractions), by slope (soil does not sit on anything steep), and by
 * the ridge field (crests are scoured, bowls collect). Free — every input is already
 * computed by the time we get here.
 */
function classify(h: number, slope: number, ridge: number, bench: number): Surface {
  // Too steep to hold soil: bare rock. Crests scour more easily than bowls.
  // The threshold is deliberately well inside the slope distribution (p90 of the
  // rideable belt) so rock comes out as coherent bands down the steep faces. Set it
  // out at the extreme tail instead and you get one isolated rock vertex here and
  // there, which on a 32 m outer ring reads as random grey slabs stuck to the hill.
  //
  // R5 moved it 0.62 -> 0.70 to hold the MIX, not the threshold. The rill field raises
  // the cross-slope gradient everywhere it runs, and at 0.62 that took rock from 14.5%
  // of the rideable belt to 31.6% — a mountain that reads grey, which is a different
  // failure from the one this round exists to fix. At 0.70 it is 24.7%, and the extra
  // ten points land where they should: on the walls of the new rills, in bands running
  // down the fall line. That is the first thing in this function whose features do NOT
  // run along the contour, which is aimed squarely at the standing "the far field reads
  // as iso-elevation banding" critique.
  if (slope > 0.52 - ridge * 0.10 + bench * 0.09) return Surface.Rock;
  if (h > SNOW_LINE + bench * 55) return Surface.Snow;
  if (h > ALPINE_LINE + bench * 45) return Surface.Loose;
  // Old-growth floor: roots on the spur crests where the big trees stand, loam in
  // the damp gully bottoms between them.
  if (h < FOREST_LINE + bench * 40) return ridge > 0.86 ? Surface.Root : Surface.Loam;
  // Open hillside: hardpack where it has been scoured, grass where it has not.
  //
  // The threshold has to be read against the field it tests. `ridge` is the ridged
  // multifractal, whose *mean* is 0.69, not 0.5 — gradient noise sits near zero most
  // of the time and (1-|n|)^2 therefore sits near one. R2 split at 0.46, which is a
  // long way down the tail: 97% of the open hillside came out hardpack and the
  // mountain was one colour. 0.655 sits just under the mean, so the split lands where
  // the terrain actually varies — hardpack scoured onto the ribs and spur noses,
  // grass held in the drainage floors and the lee slopes.
  return ridge > 0.655 ? Surface.Hardpack : Surface.Grass;
}

/** Base micro-roughness by surface, m. */
const ROUGHNESS = [0.022, 0.03, 0.05, 0.065, 0.055, 0.035, 0.028];

export function makeGroundSample(): GroundSample {
  return { height: 0, nx: 0, ny: 1, nz: 0, surface: Surface.Hardpack, trailWeight: 0, roughness: 0 };
}
